/* =============================================================================
   DAILE v2 (dv) — photo customizer controller (sections/dv-customizer.liquid).

   Built from a copy of assets/daile-customizer.js + daile-customizer-state.js
   (the customizer in production on 2026-09-28). Every proven behaviour is kept:
   quality check, normalised crop with 2.5 mm bleed, background upload of the
   original to the R2 Worker, preview + config upload, one cart/add with child
   lines (_parent_line), cart reconciliation before a retry, "Edit design" from
   the cart drawer (?edit=<_line_id>, photo from IndexedDB or the cloud), the
   bfcache return, the PDP photo hand-over, analytics event names.

   What is new:
   - a set step first (Mini 300 / Mini 500 / Classic / Signature), ?set= preselects;
   - previews are drawn by assets/dv-cz-render.js (window.DVCZ) from the owner's
     scene package (snippets/dv-cz-manifest.liquid): <set>-<orientation>-puzzle,
     mini-top-lid / classic-box-top, postcard-photo(-and-text), <set>-<o>-with-box;
   - lid compositions map onto the package layouts (see lidLayoutId);
   - config v2 in _design (redesign-docs/CUSTOMIZER-CONTRACT.md §10), v1 fields kept.

   Docs: redesign-docs/CUSTOMIZER-BUILD.md. References "old js:NNN" point to
   assets/daile-customizer.js.
   ============================================================================= */
(() => {
  'use strict';

  if (window.DVCustomizer && window.DVCustomizer.version) return;

  const VERSION = '1.3.6'; // 1.3.6 (2026-11-02): Cyrillic in Poppins / Bebas Neue / Dancing Script drawn in Montserrat / Oswald / Caveat → lid.text.print_font, card.print_font (Work v3.29 prints the same face); 1.3.5 (2026-10-22): emoji drawn from Google Noto (dv-cz-render, same pictures as Work v3.25); 1.3.4 (2026-10-22): + fonts Playfair Display, Dancing Script (lid + card; Work v3.24); 1.3.3 (2026-10-22): postcard orientation (auto = photo, or chosen) → _design.card.orientation; 1.3.2 (2026-10-22): native photo label + instant «Preparing» state, NFC link optional with https:// added; 1.1.0 (2026-09-30): lid "Large" text x 1.25 (LID_SIZE_CAPS); 1.2.0 (round 6): Large x 4/3, Small 0.913; 1.3.0 (2026-10-11): Worker uploads retried, photo.original_fallback; 1.3.1 (2026-10-12): photo.origin (DAILE STUDIO artwork) for production
  const STEPS = ['set', 'upload', 'gift', 'review'];
  const SET_KEYS = ['mini300', 'mini500', 'classic', 'signature'];

  /* Physical data per set. trim = [short, long] edge in mm (print trim, without
     bleed). Mini trims are derived from the package labels 29 × 38 / 38 × 52 cm
     and still need the owner's confirmation (CONTRACT §10.4, §14 q.1). */
  const SETS = {
    mini300: { title: 'Mini 300', pieces: 300, box: 'mini', trim: [290, 380], cm: [29, 38], inch: [11, 15] },
    mini500: { title: 'Mini 500', pieces: 500, box: 'mini', trim: [380, 520], cm: [38, 52], inch: [15, 20] },
    classic: { title: 'Classic', pieces: 1000, box: 'classic', trim: [450, 650], cm: [45, 65], inch: [18, 26] },
    signature: { title: 'Signature', pieces: 1000, box: 'classic', trim: [450, 650], cm: [45, 65], inch: [18, 26] },
  };
  const BLEED_MM = 2.5;                 // per side (old js:541-550)
  const NO_BLEED = { fx: 0, fy: 0 };
  const MAX_PHOTO_MB = 60;              // = MAX_ORIGINAL_MB of the Worker (old js:712)
  const MAX_RENDER_WIDTH = 1400;
  const LID_MAX_CHARS = 80;
  const CARD_MAX_CHARS = 300;
  /* NFC: staff write the link onto the chip by hand from the order sheet. An
     NTAG213 holds about 130 URL characters (DAILE-analysis-2026-09-25.md:1378);
     counted in UTF-8 bytes. Confirm with the chip type used in production. */
  const NFC_MAX_BYTES = 130;
  /* Request timeouts (review fix): a stalled request used to leave the section
     inert on "Adding…" for good. Cart and config calls are small. */
  const CART_TIMEOUT_MS = 30000;
  const UPLOAD_TIMEOUT_MS = 30000;
  /* Upload Worker PUTs (fix 2026-10-11, live: a customer on iPhone / 5G got
     js_err_save at ADD TO CART and left). One network failure of one PUT used
     to end the add, and the original had one total timeout counted from the
     background start (a slow but working uplink was cut at 90 s, and every
     retry started again from zero). Now every PUT is a fresh request per
     attempt (same line + token: the Worker lets the owner overwrite), retried
     on no answer / stall / 408 / 429 / 5xx; the original is cut only when it
     stops making progress (XHR upload progress), never for being slow. */
  const PUT_TRIES = 3;
  const PUT_BACKOFF_MS = [1000, 3000];
  const PUT_STALL_MS = 25000;           // no upload progress for this long = a dead connection
  const PUT_RESPONSE_MS = 45000;        // body sent, waiting for the Worker's answer (more for big bodies, see xhrPut)
  const PUT_ONLINE_WAIT_MS = 15000;     // navigator.onLine false before a retry: wait this long for "online"
  const originalCap = (blob) => Math.min(900000, Math.max(180000, Math.round(((blob && blob.size) || 0) / 10))); // ≥ 10 KB/s
  /* A picked File (iOS photo picker) or a Blob read from IndexedDB (STUDIO hand-over) may be
     backed by a file on disk that iOS can drop later; the preview, decoded at once, still shows
     while the upload fails. Photos up to this size are copied into memory when accepted. */
  const MEM_COPY_MAX_MB = 25;
  const WORKER_DEFAULT = 'https://daile-customizer-upload.hidden-surf-7be5.workers.dev';
  // Order sheets (production): staff open the assembly sheet from the Shopify admin by the hidden
  // _order_sheet link. Separate Worker; access = the same high-entropy photoToken as /photo.
  const SHEETS_DEFAULT = 'https://daile-order-sheets.hidden-surf-7be5.workers.dev';
  const SILVER = '#AAA7A3';             // package lid style colour (LID-DESIGNS-RU.md)
  const LEGACY_SILVER = '#B4B4B4';      // silver of the previous customizer
  const BLACK = '#161514';
  const SIZE_FACTORS = { s: 0.75, l: 1 };   // greeting card (unchanged)
  /* Lid text only. The style's size caps are 0.135 of the field width (maxWF)
     and 0.34 of its height (maxHF); the text is shrunk to fit under them, then
     multiplied by the preset factor.
     Round 5 (owner 2026-09-30): "Large" caps x 1.25 (portrait lid on the 1440
     desktop preview 24.6 -> 30.7 px).
     Round 6 (owner 2026-09-30, "again"): "Large" +2 px and "Small" +4 px on the
     same preview —
       Large: caps x 4/3 -> maxWF 0.18, maxHF 0.45333, factor 1   (30.7 -> 32.7 px)
       Small: caps x 1   -> maxWF 0.135, maxHF 0.34, factor 0.913 (18.4 -> 22.4 px)
     Stored values stay 's' | 'l'; print follows lid.text.size_mm / lines
     (CUSTOMIZER-CONTRACT.md §10.7). */
  const LID_SIZE_CAPS = { s: 1, l: 4 / 3 };
  const LID_SIZE_FACTORS = { s: 0.913, l: 1 };
  const FONTS = {
    cormorant: { family: 'Cormorant Garamond', weight: 300 }, // = print font (CormorantGaramond Light)
    poppins: { family: 'Poppins', weight: 400 },
    bebas: { family: 'Bebas Neue', weight: 400 },
    playfair: { family: 'Playfair Display', weight: 400 },   // 1.3.4 (owner 22.10): + Work v3.24 prints it
    dancing: { family: 'Dancing Script', weight: 400 },      // 1.3.4: script face for messages
    hanken: { family: 'Hanken Grotesk', weight: 500 },       // legacy designs only
  };
  /* 1.3.6 (2026-11-02, owner, order #1288): Poppins, Bebas Neue and Dancing Script have no Cyrillic. The canvas drew
     the missing letters in the fallback Georgia, and Work could not print them. Now a text with Cyrillic is drawn whole
     in a close face that has it, and the design records it (lid.text.print_font / card.print_font) — Work v3.29 prints
     exactly that face, so the preview is the print. Fonts: Google Fonts (OFL), loaded in sections/dv-customizer.liquid. */
  const CYR_FONTS = {
    poppins: { family: 'Montserrat', weight: 400, print: 'montserrat' },
    bebas: { family: 'Oswald', weight: 400, print: 'oswald' },
    dancing: { family: 'Caveat', weight: 400, print: 'caveat' },
  };
  const CYRILLIC = /[\u0400-\u052F\u1C80-\u1C8F\u2DE0-\u2DFF\uA640-\uA69F]/;
  /* The face a text is drawn and printed in: the chosen font, or its Cyrillic stand-in. → { family, weight, print } */
  function faceFor(key, text) {
    const cyr = CYR_FONTS[key];
    if (cyr && CYRILLIC.test(String(text || ''))) return cyr;
    const f = FONTS[key] || FONTS.cormorant;
    return { family: f.family, weight: f.weight, print: null };
  }
  /* Warn "text is getting small" below this size, in 3919-px scene units of the
     flat lid scene (≈ 4 mm type on the 311 mm CLASSIC lid). */
  const LID_TEXT_WARN_UNITS = 34;

  /* Flat lid geometry for the config snapshot (CONTRACT §10.5):
     [x0, y0, x1, y1] as fractions of the flat lid, origin top-left.
     CLASSIC = Customizer Assets/designs/classic/layouts.json (311 × 231 mm).
     MINI = preliminary values from the old whole-lid mask (size not measured yet). */
  const LID_SIZE_MM = { classic: [311, 231], mini: null };
  const LID_RECTS = {
    classic: {
      'horizontal-logo': { photo: [0.175, 0.06326, 0.825, 0.67227], text: [0.50025, 0.69347, 0.9725, 0.97813] },
      'horizontal-no-logo': { photo: [0.175, 0.06326, 0.825, 0.67227], text: [0.03125, 0.69347, 0.97275, 0.97813] },
      'vertical-no-logo': { photo: [0.07424, 0.09996, 0.48608, 0.89983], text: [0.52824, 0.1005, 0.95704, 0.89778] },
      'vertical-logo': { photo: [0.074, 0.1, 0.48629, 0.89962], text: [0.52829, 0.40769, 0.957, 0.89769] },
    },
    mini: {
      vertical: { photo: [0.238, 0.0292, 0.7637, 0.7352], text: [0.0356, 0.753, 0.9639, 0.978] },
      horizontal: { photo: [0.051, 0.0525, 0.9516, 0.6708], text: [0.051, 0.7004, 0.9516, 0.9758] },
    },
  };
  const CARD_SIZE_MM = [160, 220];      // package postcard scenes (16 × 22 cm); print uses 166 × 230 (CONTRACT §10.8)
  /* The package postcard text field has no style: the old card style is used
     (font / size / colour / alignment chosen by the customer). */
  const CARD_TEXT_STYLE = {
    font_family: 'Cormorant Garamond', font_weight: 300, fallback_font_family: 'Georgia',
    color: BLACK, align: 'justify', padding_fraction: 0.07, max_lines: 18, line_height: 1.28,
    font_size_max_width_fraction: 0.1, font_size_max_height_fraction: 0.075,
  };
  const CARD_GHOST = 'rgba(22, 21, 20, 0.45)';

  /* --------------------------------------------------------------- helpers */

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, Number.isFinite(v) ? v : 0));
  const round = (v, d = 4) => Math.round(v * 10 ** d) / 10 ** d;
  const orientName = (o) => (o === 'h' ? 'horizontal' : 'vertical');
  const iso = (s) => '\u2068' + s + '\u2069'; // bidi isolate (FSI … PDI) for numbers / Latin text inside RTL lines

  function track(eventName, params) {
    // Same channels as the old customizer (old js:30-45): dataLayer + Custom Pixel.
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...(params || {}) });
    } catch (e) { /* analytics must never break the flow */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, params || {});
      }
    } catch (e2) { /* ignore */ }
  }

  function createStore(initial) {
    // Same contract as daile-customizer-state.js: set (shallow) / merge (branch) / subscribe.
    let state = initial;
    const listeners = [];
    const notify = () => listeners.forEach((fn) => {
      try { fn(state); } catch (e) { console.error('[dv-customizer] subscriber failed', e); }
    });
    return {
      getState: () => state,
      set(patch) { state = { ...state, ...patch }; notify(); },
      merge(key, patch) { this.set({ [key]: { ...(state[key] || {}), ...patch } }); },
      subscribe(fn) {
        listeners.push(fn);
        return () => { const i = listeners.indexOf(fn); if (i > -1) listeners.splice(i, 1); };
      },
    };
  }

  const defaultBox = () => ({ variant: 'default', position: 'center', text: '', font: 'cormorant', sizePreset: 'l', color: SILVER });
  // 1.3.3: orient 'auto' (= the photo's orientation) | 'v' | 'h' — the postcard follows a landscape photo by default
  const defaultCard = () => ({ added: false, text: '', font: 'cormorant', sizePreset: 'l', color: BLACK, align: 'justify', orient: 'auto' });

  function initialState() {
    return {
      step: 0,
      maxStepReached: 0,
      set: null,
      photo: null,                              // { width, height, quality } of the ORIGINAL file
      crop: { zoom: 1, nx: 0, ny: 0 },          // normalised to the puzzle zone (old contract)
      orientation: 'v',
      box: defaultBox(),
      nfc: { enabled: false, url: '' },
      card: defaultCard(),
      addons: { glue: false },
      extras: { bag: false, magnets: false, frameInterest: false }, // legacy designs only
      /* Edit from the cart: child lines the cart drawer attached to the edited
         line (bag, glue on a non-Mini line…), re-created under the new line.
         [{ id, quantity, properties, title, price }] — review fix: they used to
         be deleted with the old line. Not part of _design (the order lines are
         the truth for add-ons, CONTRACT §10.3). */
      kept: [],
    };
  }

  /* ------------------------------------------------------------ crop maths
     Zone units: width = aspect, height = 1. Crop offsets nx / ny are fractions
     of the zone width / height (old js:561-594, production lib/geometry.js).
     The photo always covers the zone plus its bleed. */

  function trimFor(set, o) {
    const [s, l] = SETS[set || 'classic'].trim;
    return o === 'h' ? [l, s] : [s, l];
  }

  function bleedFor(set, o) {
    const [w, h] = trimFor(set, o);
    return { fx: (2 * BLEED_MM) / w, fy: (2 * BLEED_MM) / h };
  }

  function coverScale(pw, ph, aspect, zoom, bleed) {
    return Math.max((aspect * (1 + bleed.fx)) / pw, (1 + bleed.fy) / ph) * Math.max(1, zoom || 1);
  }

  function clampCrop(pw, ph, aspect, crop, bleed = NO_BLEED) {
    const zoom = clamp(Number(crop && crop.zoom) || 1, 1, 3);
    const s = coverScale(pw, ph, aspect, zoom, bleed);
    const maxX = Math.max(0, (pw * s - aspect * (1 + bleed.fx)) / 2) / aspect;
    const maxY = Math.max(0, (ph * s - (1 + bleed.fy)) / 2);
    return { zoom, nx: clamp(Number(crop && crop.nx) || 0, -maxX, maxX), ny: clamp(Number(crop && crop.ny) || 0, -maxY, maxY) };
  }

  /** Visible window [x, y, w, h] as fractions of the photo (DVCZ cropBySlot). */
  function cropRect(pw, ph, aspect, crop, bleed = NO_BLEED) {
    const c = clampCrop(pw, ph, aspect, crop, bleed);
    const s = coverScale(pw, ph, aspect, c.zoom, bleed);
    const sw = aspect / s;
    const sh = 1 / s;
    const sx = (pw - sw) / 2 - (c.nx * aspect) / s;
    const sy = (ph - sh) / 2 - c.ny / s;
    return [round(sx / pw, 6), round(sy / ph, 6), round(sw / pw, 6), round(sh / ph, 6)];
  }

  /** Pixels of the original inside the printed trim (old js:59-66, per set). */
  function croppedPixels(st) {
    if (!st.photo) return { width: 0, height: 0 };
    const [w, h] = trimFor(st.set, st.orientation);
    const zoom = Math.max(1, Number(st.crop && st.crop.zoom) || 1);
    const s = Math.max((w + 2 * BLEED_MM) / st.photo.width, (h + 2 * BLEED_MM) / st.photo.height) * zoom;
    return { width: w / s, height: h / s };
  }

  /* Quality thresholds of the old customizer (old js:50-57) were written for
     65 × 45 cm. They are scaled by the trim of the chosen set, so Classic and
     Signature keep exactly the old behaviour and Mini gets the same print dpi. */
  function qualityFor(w, h, set) {
    const [ts, tl] = SETS[set || 'classic'].trim;
    const ls = tl / 650;
    const ss = ts / 450;
    const long = Math.max(w, h);
    const short = Math.min(w, h);
    if (long < 800 * ls || short < 600 * ss) return 'blocked';
    if (long > 3500 * ls && short > 2500 * ss) return 'green';
    if (long >= 1500 * ls && short >= 1000 * ss) return 'yellow';
    return 'red';
  }

  const fileQuality = (w, h) => qualityFor(w, h, 'classic'); // absolute 800 × 600 px floor for any file
  const cropQuality = (st) => { const p = croppedPixels(st); return qualityFor(p.width, p.height, st.set); };

  function utf8Length(s) {
    try { return new TextEncoder().encode(s).length; } catch (e) { return s.length; }
  }
  /** '' when the NFC link is usable, else 'invalid' | 'long'. The whole string
     must be one URL: scheme, a host with a dot, then an optional path without
     spaces (the old test also passed "https://example.com/my song"). */
  /* 1.3.2 (22.10, analytics: «Bitte geben Sie einen gültigen Link ein» stopped a German buyer at minute 18):
     the link is optional, and «youtube.com/…» / «www.instagram.com/…» get https:// added by us. */
  function nfcNormalize(u) {
    let s = String(u || '').trim();
    if (!s) return '';
    if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) s = 'https://' + s.replace(/^\/+/, '');
    return s;
  }
  function nfcProblem(u) {
    const s = nfcNormalize(u);
    if (!s) return '';
    if (!/^https?:\/\/[^\s/?#]+\.[^\s/?#]+(?:[/?#]\S*)?$/i.test(s)) return 'invalid';
    if (utf8Length(s) > NFC_MAX_BYTES) return 'long';
    return '';
  }
  const validNfcUrl = (u) => !nfcProblem(u);

  /** fetch with a timeout: aborts the request after ms (and when opts.signal aborts). */
  function fetchT(url, opts, ms) {
    if (!ms || !('AbortController' in window)) return fetch(url, opts);
    const ac = new AbortController();
    const outer = opts && opts.signal;
    if (outer) {
      if (outer.aborted) ac.abort();
      else outer.addEventListener('abort', () => ac.abort(), { once: true });
    }
    const timer = window.setTimeout(() => ac.abort(), ms);
    return fetch(url, { ...(opts || {}), signal: ac.signal }).then((r) => {
      window.clearTimeout(timer);
      return r;
    }, (error) => {
      window.clearTimeout(timer);
      throw error;
    });
  }

  const putError = (kind, message) => {
    const error = new Error(message);
    error.kind = kind; // 'network' | 'stall' | 'timeout' | 'abort' | 'source'
    return error;
  };

  /** One PUT as XMLHttpRequest: fetch has no upload progress, and progress is what tells a slow
     uplink (keep going) from a dead one (start again). Resolves { status, text } for any HTTP
     answer; rejects putError('network' | 'stall' | 'timeout' | 'abort'). */
  function xhrPut(url, body, type, opts) {
    const o = opts || {};
    return new Promise((resolve, reject) => {
      if (o.signal && o.signal.aborted) { reject(putError('abort', 'aborted')); return; }
      const xhr = new XMLHttpRequest();
      const t0 = Date.now();
      /* "Sent" means handed to the network stack: a slow uplink may still be draining its
         buffers, so the answer gets at least 45 s, or the body at ~20 KB/s. */
      const size = (body && (body.size || body.length)) || 0;
      const answerMs = Math.max(PUT_RESPONSE_MS, Math.round(size / 20));
      let last = t0;
      let sent = false;
      let done = false;
      let tick = 0;
      const onAbort = () => fail('abort', 'aborted');
      // iOS freezes timers and the network in the background: a fresh window once visible again.
      const onVisible = () => { if (document.visibilityState === 'visible') last = Date.now(); };
      const finish = () => {
        done = true;
        window.clearInterval(tick);
        document.removeEventListener('visibilitychange', onVisible);
        if (o.signal) o.signal.removeEventListener('abort', onAbort);
      };
      function fail(kind, message) {
        if (done) return;
        finish();
        try { xhr.abort(); } catch (e) { /* ignore */ }
        reject(putError(kind, message));
      }
      tick = window.setInterval(() => {
        const now = Date.now();
        if (o.capMs && now - t0 > o.capMs) fail('timeout', 'timeout after ' + o.capMs + ' ms');
        else if (now - last > (sent ? answerMs : (o.stallMs || PUT_STALL_MS))) fail('stall', (sent ? 'no answer' : 'no upload progress') + ' for ' + Math.round((now - last) / 1000) + ' s');
      }, 1000);
      document.addEventListener('visibilitychange', onVisible);
      if (o.signal) o.signal.addEventListener('abort', onAbort, { once: true });
      xhr.upload.onprogress = (e) => {
        last = Date.now();
        if (o.onProgress && e.lengthComputable && e.total) o.onProgress(e.loaded / e.total);
      };
      xhr.upload.onload = () => { sent = true; last = Date.now(); };
      xhr.onload = () => {
        if (done) return;
        finish();
        resolve({ status: xhr.status, text: String(xhr.responseText || '').slice(0, 200) });
      };
      xhr.onerror = () => fail('network', 'network error'); // Safari "Load failed", Chrome "Failed to fetch"
      xhr.onabort = () => fail('abort', 'aborted');
      try {
        xhr.open('PUT', url, true);
        xhr.setRequestHeader('Content-Type', type);
        xhr.send(body);
      } catch (e) {
        fail('network', 'send failed: ' + ((e && e.message) || e));
      }
    });
  }

  const retryableStatus = (s) => s === 408 || s === 425 || s === 429 || s >= 500;

  function sleepFor(ms, signal) {
    return new Promise((resolve) => {
      const timer = window.setTimeout(resolve, ms);
      if (signal) signal.addEventListener('abort', () => { window.clearTimeout(timer); resolve(); }, { once: true });
    });
  }
  /** Resolves at once when the browser thinks it is online, else on "online" or after ms. */
  function onlineOrAfter(ms) {
    if (navigator.onLine !== false) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => { window.clearTimeout(timer); window.removeEventListener('online', done); resolve(); };
      const timer = window.setTimeout(done, ms);
      window.addEventListener('online', done);
    });
  }

  /** PUT to the upload Worker with retries; every attempt is a fresh request.
     nextBody(prev) -> { data, type } (or a Promise of it); prev = { status, kind } of the attempt
     that just failed (null on the first one), so the caller can switch the source.
     Resolves { status, attempts }. Rejects an Error "upload <stage> failed" (no HTTP answer) or
     "upload <stage> <status>", with .stage, .status (0 = no answer), .attempts, .detail, .cause,
     .conflict (409 "owned by another session"), .aborted. Messages keep the word "upload": the
     add-to-cart catch maps them to js_err_save. */
  function putWithRetry(stage, url, nextBody, opts) {
    const o = opts || {};
    const tries = o.tries || PUT_TRIES;
    let attempt = 0;
    const fail = (status, cause, detail) => {
      const error = new Error('upload ' + stage + (status ? ' ' + status : ' failed'));
      error.stage = stage;
      error.status = status || 0;
      error.attempts = attempt;
      error.detail = detail || '';
      if (cause) error.cause = cause;
      if (status === 409) error.conflict = true;
      if (cause && cause.kind === 'abort') error.aborted = true;
      return error;
    };
    const again = (prev) => sleepFor(PUT_BACKOFF_MS[Math.min(attempt - 1, PUT_BACKOFF_MS.length - 1)], o.signal)
      .then(() => onlineOrAfter(PUT_ONLINE_WAIT_MS))
      .then(() => {
        if (o.signal && o.signal.aborted) throw fail(0, putError('abort', 'aborted'));
        return once(prev);
      });
    function once(prev) {
      attempt += 1;
      return Promise.resolve().then(() => nextBody(prev))
        .then((body) => xhrPut(url, body.data, body.type, o))
        .then((res) => {
          if (res.status >= 200 && res.status < 300) return { status: res.status, attempts: attempt };
          if (res.status === 409) throw fail(409, null, res.text);
          if (attempt < tries && (retryableStatus(res.status) || (o.retryStatus && o.retryStatus(res.status)))) {
            console.warn('[dv-customizer] upload ' + stage + ' attempt ' + attempt + ': HTTP ' + res.status + ', retrying');
            return again({ status: res.status, kind: 'http' });
          }
          throw fail(res.status, null, res.text);
        }, (error) => {
          const kind = (error && error.kind) || 'source';
          if (kind === 'abort' || kind === 'source' || attempt >= tries) throw fail(0, error);
          console.warn('[dv-customizer] upload ' + stage + ' attempt ' + attempt + ': ' + kind + ' (' + error.message + '), retrying');
          return again({ status: 0, kind });
        });
    }
    return once(null);
  }

  /** Can this Blob still be read? (iOS can drop the file behind a picked File or an IndexedDB
     Blob.) Unknown (no answer in 5 s) counts as readable. */
  function blobReadable(blob) {
    if (!blob || blob.dvInMemory) return Promise.resolve(!!blob);
    return new Promise((resolve) => {
      const timer = window.setTimeout(() => resolve(true), 5000);
      try {
        const fr = new FileReader();
        fr.onload = () => { window.clearTimeout(timer); resolve(true); };
        fr.onerror = () => { window.clearTimeout(timer); resolve(false); };
        fr.readAsArrayBuffer(blob.slice(0, 64 * 1024));
      } catch (e) { window.clearTimeout(timer); resolve(false); }
    });
  }

  /** In-memory copy of a photo (see MEM_COPY_MAX_MB). Falls back to the Blob itself. */
  function memoryCopy(blob) {
    if (!blob || blob.dvInMemory || blob.size > MEM_COPY_MAX_MB * 1024 * 1024) return Promise.resolve(blob);
    const read = typeof blob.arrayBuffer === 'function'
      ? blob.arrayBuffer()
      : new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = () => reject(fr.error);
        fr.readAsArrayBuffer(blob);
      });
    return read.then((buf) => {
      const copy = new Blob([buf], { type: blob.type });
      copy.dvInMemory = true;
      return copy;
    }, (e) => {
      console.warn('[dv-customizer] photo not copied to memory', e);
      return blob;
    });
  }

  /** JPEG of a prepared photo canvas, encoded once per canvas. */
  function canvasJpeg(canvas) {
    if (!canvas) return Promise.reject(putError('source', 'no photo canvas'));
    if (!canvas.dvJpeg) {
      canvas.dvJpeg = new Promise((resolve, reject) => {
        const done = (blob) => (blob && blob.size ? resolve(blob) : reject(putError('source', 'canvas JPEG encode failed')));
        if (canvas.toBlob) canvas.toBlob(done, 'image/jpeg', 0.92);
        else fetch(canvas.toDataURL('image/jpeg', 0.92)).then((r) => r.blob()).then(done, reject);
      }).catch((e) => {
        canvas.dvJpeg = null;
        throw e;
      });
    }
    return canvas.dvJpeg;
  }

  /** "message <- cause" chain for logs and diagnostics. */
  function describeError(e) {
    let s = '';
    let c = e;
    for (let i = 0; c && i < 4; i += 1) {
      s += (i ? ' <- ' : '') + (c.name && c.name !== 'Error' ? c.name + ': ' : '') + (c.message || String(c)) + (c.detail ? ' ' + c.detail : '');
      c = c.cause;
    }
    return s.slice(0, 300);
  }

  function randHex(n) {
    const a = new Uint8Array(n);
    (window.crypto || window.msCrypto).getRandomValues(a);
    return Array.from(a, (b) => ('0' + b.toString(16)).slice(-2)).join('');
  }
  const newLineId = () => 'cz-' + Date.now().toString(36) + '-' + randHex(4); // Worker: ^cz-[a-z0-9]+-[a-z0-9]{3,12}$

  function decodeEntities(s) {
    if (typeof s !== 'string' || !/&[#a-z0-9]+;/i.test(s)) return s;
    const ta = document.createElement('textarea');
    ta.innerHTML = s; // RCDATA: entities only, nothing executes
    return ta.value;
  }

  const idle = (fn) => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 1500 }) : window.setTimeout(fn, 400));

  /* ------------------------------------------------------- IndexedDB (old js:412-460) */

  function idbOpen() {
    return new Promise((resolve, reject) => {
      try {
        const rq = window.indexedDB.open('daile-cz', 1); // same DB as daile-pdp.js and the old customizer
        rq.onupgradeneeded = () => rq.result.createObjectStore('photos');
        rq.onsuccess = () => resolve(rq.result);
        rq.onerror = () => reject(rq.error);
      } catch (e) { reject(e); }
    });
  }
  function idbPut(key, val) {
    return idbOpen().then((db) => new Promise((resolve, reject) => {
      const tx = db.transaction('photos', 'readwrite');
      tx.objectStore('photos').put(val, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    }));
  }
  function idbGet(key) {
    return idbOpen().then((db) => new Promise((resolve, reject) => {
      const rq = db.transaction('photos').objectStore('photos').get(key);
      rq.onsuccess = () => resolve(rq.result || null);
      rq.onerror = () => reject(rq.error);
    }));
  }
  function idbSavePhoto(lineId, file) {
    return idbPut('photo_' + lineId, file).catch((e1) => {
      console.warn('[dv-customizer] File not stored in IndexedDB, retrying as ArrayBuffer', e1);
      return new Promise((resolve) => {
        const fr = new FileReader();
        fr.onload = () => idbPut('photo_' + lineId, { buf: fr.result, type: file.type }).then(resolve, (e2) => {
          console.error('[dv-customizer] idb save (fallback)', e2); resolve();
        });
        fr.onerror = () => { console.error('[dv-customizer] FileReader', fr.error); resolve(); };
        fr.readAsArrayBuffer(file);
      });
    });
  }
  const recordToBlob = (rec) => (rec && rec.buf ? new Blob([rec.buf], { type: rec.type || 'image/jpeg' }) : rec);

  /* ------------------------------------------------------- cart (old js:73-130) */

  function reconcileSubmission(shopRoot, lineId, submission) {
    if (submission.confirmed) return Promise.resolve(true);
    if (!submission.attempted) return Promise.resolve(false);
    return fetchT(shopRoot + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }, CART_TIMEOUT_MS)
      .then((response) => {
        if (!response.ok) throw new Error('cart verification failed');
        return response.json();
      })
      .then((cart) => {
        if (!Array.isArray(cart.items)) throw new Error('cart verification failed');
        const matches = cart.items.filter((item) => {
          const p = item.properties || {};
          return p._line_id === lineId || p._parent_line === lineId;
        });
        if (!matches.length) {
          submission.attempted = false;
          return false;
        }
        const complete = matches.length === submission.items.length && submission.items.every((expected) => matches.some((item) => {
          const p = item.properties || {};
          return Number(item.variant_id || item.id) === Number(expected.id) && item.quantity === expected.quantity &&
            Object.keys(expected.properties).every((key) => p[key] === expected.properties[key]);
        }));
        if (!complete) throw new Error('cart verification incomplete');
        submission.confirmed = true;
        return true;
      })
      .catch((error) => {
        const verificationError = new Error('cart verification failed');
        verificationError.cause = error;
        throw verificationError;
      });
  }

  function saveDesignAndAddItems(options, submission) {
    return reconcileSubmission(options.shopRoot, options.lineId, submission).then((alreadyAdded) => {
      if (alreadyAdded) return undefined;
      // Retried (fresh PUT each time); a 409 rejects with .conflict and the caller moves to a new line.
      return putWithRetry('config', options.configUrl, () => ({ data: options.design, type: 'application/json' }), {
        capMs: UPLOAD_TIMEOUT_MS, stallMs: 20000,
      }).then(() => {
        submission.items = options.items;
        submission.attempted = true;
        // A timed-out add is ambiguous (it may have reached Shopify): the retry goes through reconcileSubmission.
        return fetchT(options.shopRoot + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: options.items }),
        }, CART_TIMEOUT_MS);
      }).then((response) => {
        if (!response.ok) {
          // A rejected cart/add (e.g. 422 sold out) created nothing: allow a clean retry (fixes old bug B2).
          // Shopify's description names the product (e.g. a sold-out add-on): keep it for the message.
          return response.json().catch(() => ({})).then((body) => {
            const addError = new Error('cart/add ' + response.status);
            addError.status = response.status;
            addError.description = body && typeof body.description === 'string' ? body.description : '';
            throw addError;
          });
        }
        submission.confirmed = true;
        return undefined;
      });
    });
  }

  /* ======================================================================
     One customizer instance per section root.
     ====================================================================== */

  function mount(scope) {
    const root = scope.matches && scope.matches('[data-dv-customizer]') ? scope : scope.querySelector('[data-dv-customizer]');
    if (!root || root.dvCzMounted) return null;
    if (!window.DVCZ || !window.DVCZ.version) {
      console.error('[dv-customizer] assets/dv-cz-render.js is not loaded');
      return null;
    }
    root.dvCzMounted = true;

    const cleanups = [];
    const on = (target, type, fn, opts) => {
      if (!target) return;
      target.addEventListener(type, fn, opts);
      cleanups.push(() => target.removeEventListener(type, fn, opts));
    };
    const $ = (sel) => root.querySelector(sel);
    const $$ = (sel) => Array.from(root.querySelectorAll(sel));
    const readJSON = (sel) => {
      try { return JSON.parse(($(sel) || {}).textContent || 'null'); } catch (e) {
        console.error('[dv-customizer] bad JSON in ' + sel, e);
        return null;
      }
    };

    const CONFIG = readJSON('[data-dv-cz-config]') || {};
    const RAW_STR = readJSON('[data-dv-cz-strings]') || {};
    const STR = {};
    Object.keys(RAW_STR).forEach((k) => { if (RAW_STR[k] && RAW_STR[k] !== k) STR[k] = decodeEntities(RAW_STR[k]); });
    const t = (key, fallback, vars) => {
      let s = STR[key] || fallback || '';
      if (vars) s = s.replace(/\{(\w+)\}/g, (m, name) => (name in vars ? String(vars[name]) : m));
      return s;
    };
    const nfcMessage = (issue) => (issue === 'long'
      ? t('dv_cz_js_nfc_long', 'This link is too long for the NFC chip — please use a shorter link (up to {n} characters).', { n: NFC_MAX_BYTES })
      : t('cz_nfc_invalid', 'Please enter a valid link starting with http(s)://'));

    let manifest;
    try {
      manifest = window.DVCZ.loadManifest($('[data-dv-cz-manifest]'));
    } catch (e) {
      console.error('[dv-customizer] scene manifest missing', e);
      return null;
    }
    // Greeting-card text: the package field has no style/aspect (CUSTOMIZER-ASSETS §8 q.1).
    const cardScene = manifest.scenes['postcard-photo-and-text'];
    const cardSlot = cardScene && cardScene.text_slots && cardScene.text_slots.message;
    if (cardSlot && !cardSlot.style) {
      const size = cardScene.physical_size_cm || [16, 22];
      cardSlot.aspect = size[0] / size[1];
      cardSlot.style = { ...CARD_TEXT_STYLE, padding_fraction: cardSlot.padding_normalized || CARD_TEXT_STYLE.padding_fraction };
    }
    /* 1.3.3 (owner 22.10: «a landscape photo → a landscape postcard, the text written across»): the same card turned
       90° — scene postcard-photo-and-text-h (the photo scene rotated, snippets/dv-cz-manifest), 22 × 16 cm. */
    const cardSceneH = manifest.scenes['postcard-photo-and-text-h'];
    const cardSlotH = cardSceneH && cardSceneH.text_slots && cardSceneH.text_slots.message;
    if (cardSlotH && !cardSlotH.style) {
      const sizeH = cardSceneH.physical_size_cm || [22, 16];
      cardSlotH.aspect = sizeH[0] / sizeH[1];
      cardSlotH.style = { ...CARD_TEXT_STYLE, padding_fraction: cardSlotH.padding_normalized || CARD_TEXT_STYLE.padding_fraction };
    }

    const SET_CFG = CONFIG.sets || {};
    const ADDONS = CONFIG.addons || {};
    const CURRENCY = CONFIG.currency || 'EUR';
    const LOCALE = CONFIG.locale || document.documentElement.lang || 'en';
    const PDP_URL = CONFIG.pdp_url || '/products/custom-puzzle';
    const WORKER = String(CONFIG.worker || WORKER_DEFAULT).replace(/\/$/, '');
    const SHOP_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const CARD_SAMPLE = CONFIG.card_sample || 'To someone special,\n\nEvery piece of this puzzle is a memory we share.\n\nWith love, always';
    const PREVIEW_UNAVAILABLE = !!CONFIG.preview_unavailable || /[?&]cz_preview=1\b/.test(window.location.search);
    const reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

    const setStatus = (key) => (SET_CFG[key] && SET_CFG[key].status) || 'missing';
    /* An add-on can be offered when it has a variant that is on sale: glue-kit
       tracks stock and denies overselling (CONTRACT §8). "available" comes from
       Liquid; configs without the flag count as available. */
    const addonOk = (a) => !!(ADDONS[a] && Number(ADDONS[a].variant_id) && ADDONS[a].available !== false);
    const purchasable = (key) => !!(key && SET_CFG[key] && SET_CFG[key].status === 'ok' && SET_CFG[key].variant_id);
    const selectable = (key) => !!(key && SETS[key] && (purchasable(key) || PREVIEW_UNAVAILABLE));
    const isMini = (key) => !!key && SETS[key].box === 'mini';

    function fmtMoney(cents) {
      const amount = (Number(cents) || 0) / 100;
      try {
        return new Intl.NumberFormat(LOCALE, {
          style: 'currency', currency: CURRENCY, numberingSystem: 'latn', // Latin digits, like fmtNum and daile-money
          minimumFractionDigits: amount % 1 === 0 ? 0 : 2, maximumFractionDigits: 2,
        }).format(amount);
      } catch (e) {
        return CURRENCY + ' ' + amount.toFixed(2);
      }
    }
    /* Display-only number format (1,000 / 1.000); cart properties keep the raw
       numbers. Latin digits, like the sizes and prices around them. */
    let numFormat = null;
    try { numFormat = new Intl.NumberFormat(LOCALE, { numberingSystem: 'latn' }); } catch (e) { numFormat = null; }
    const fmtNum = (n) => (numFormat ? numFormat.format(n) : String(n));
    const priceOf = (item) => (item && Number(item.price)) || 0;
    const fullOf = (item) => { const p = priceOf(item); const c = (item && Number(item.compare_at)) || 0; return c > p ? c : p; };
    const priceHtml = (item, plus) => {
      const p = priceOf(item);
      const f = fullOf(item);
      // data-mn-price-cents: DAILE Friday code → assets/mn-dfriday.js shows −30% (compare-at hidden then).
      return '<span dir="ltr">' + (plus ? '+' : '') + (f > p ? '<s data-mn-df-hide>' + fmtMoney(f) + '</s> ' : '') + '<span data-mn-price-cents="' + p + '">' + fmtMoney(p) + '</span></span>';
    };

    /* ------------------------------------------------------------- DOM */
    const els = {
      progress: $('[data-dv-cz-progress]'),
      progressBtns: $$('[data-dv-cz-step]'),
      panels: $$('[data-dv-cz-panel]'),
      builderHeading: $('[data-dv-cz-builder-heading]'),
      chosen: $('[data-dv-cz-chosen]'),
      chosenImg: $('[data-dv-cz-chosen-img]'),
      chosenName: $('[data-dv-cz-chosen-name]'),
      chosenSpec: $('[data-dv-cz-chosen-spec]'),
      previewBanner: $('[data-dv-cz-preview-banner]'),
      setNotice: $('[data-dv-cz-set-notice]'),
      setCards: $$('[data-dv-cz-set]'),
      nextBtns: $$('[data-dv-cz-next]'),
      gateHints: $$('[data-dv-cz-gate-hint]'),
      backBtns: $$('[data-dv-cz-back]'),
      orientBtns: $$('[data-dv-cz-orient]'),
      size: $('[data-dv-cz-size]'),
      file: $('[data-dv-cz-file]'),
      dropzone: $('[data-dv-cz-dropzone]'),
      uploadError: $('[data-dv-cz-upload-error]'),
      editNote: $('[data-dv-cz-edit-note]'),
      photoControls: $('[data-dv-cz-photo-controls]'),
      quality: $('[data-dv-cz-quality]'),
      zoom: $('[data-dv-cz-zoom]'),
      zone: $('[data-dv-cz-zone]'),
      proxy: $('[data-dv-cz-proxy]'),
      proxyPhoto: $('[data-dv-cz-proxy-photo]'),
      proxyRelief: $('[data-dv-cz-proxy-relief]'),
      gestureHint: $('[data-dv-cz-gesture-hint]'),
      giftTabs: $$('[data-dv-cz-gift-tab]'),
      giftSubs: $$('[data-dv-cz-gift-sub]'),
      badgeBox: $('[data-dv-cz-gift-badge="box"]'),
      badgeCard: $('[data-dv-cz-gift-badge="card"]'),
      boxTypeGroups: $$('[data-dv-cz-box-type]'),
      textControls: $('[data-dv-cz-text-controls]'),
      lidText: $('[data-dv-cz-text]'),
      lidCount: $('[data-dv-cz-lid-count]'),
      lidNote: $('[data-dv-cz-lid-note]'),
      lidError: $('[data-dv-cz-lid-error]'),
      nfcRow: $('[data-dv-cz-nfc-row]'),
      nfcUrl: $('[data-dv-cz-nfc-url]'),
      nfcError: $('[data-dv-cz-nfc-error]'),
      cardInput: $('[data-dv-cz-card-input]'),
      cardCount: $('[data-dv-cz-card-count]'),
      cardHint: $('[data-dv-cz-card-hint]'),
      cardError: $('[data-dv-cz-card-error]'),
      rwSet: $('[data-dv-cz-rw-set]'),
      rwOrient: $('[data-dv-cz-rw-orient]'),
      rwBase: $('[data-dv-cz-rw-base]'),
      rwBox: $('[data-dv-cz-rw-box]'),
      rwBoxPrice: $('[data-dv-cz-rw-box-price]'),
      rwBoxX: $('[data-dv-cz-rw-remove="nodaile"]'),
      rwNfcRow: $('[data-dv-cz-rw-nfc-row]'),
      rwNfc: $('[data-dv-cz-rw-nfc]'),
      rwCardRow: $('[data-dv-cz-rw-card-row]'),
      rwCard: $('[data-dv-cz-rw-card]'),
      rwCardPrice: $('[data-dv-cz-rw-card-price]'),
      rwGlueRow: $('[data-dv-cz-rw-glue-row]'),
      rwGluePrice: $('[data-dv-cz-rw-glue-price]'),
      rwGlueAdd: $('[data-dv-cz-glue-add]'),
      rwGlueX: $('[data-dv-cz-rw-remove="glue"]'),
      rwBagRow: $('[data-dv-cz-rw-bag-row]'),
      rwBagPrice: $('[data-dv-cz-rw-bag-price]'),
      rwMagnetsRow: $('[data-dv-cz-rw-magnets-row]'),
      rwMagnetsPrice: $('[data-dv-cz-rw-magnets-price]'),
      rwList: $('[data-dv-cz-rw-list]'),
      rwTotal: $('[data-dv-cz-rw-total]'),
      navTotal: $('[data-dv-cz-nav-total]'),
      atcError: $('[data-dv-cz-atc-error]'),
      // Outside the <section>: the section is inert while adding to cart (setAtcBusyUi).
      live: (root.parentElement && root.parentElement.querySelector('[data-dv-cz-live]')) || null,
      nfcDialog: $('[data-dv-cz-dialog="nfc"]'),
      confirmDialog: $('[data-dv-cz-dialog="confirm"]'),
    };

    const store = createStore(initialState());
    let giftTab = 'box';
    let returnStep = null;          // step to come back to after "Change set"
    let photoCanvas = null;         // DVCZ.preparePhoto copy (previews only; the original goes to R2)
    let photoVersion = 0;
    let proxyUrl = null;
    let currentFile = null;
    let editingLineId = null;
    let uploadSeq = 0;
    let confirmPending = null;      // 'remove' | 'replace' | {file}
    let pendingReset = false;
    let gestureHintDone = false;
    let reviewTracked = false;
    let textTracked = false;
    let renderDelay = 0;
    let lidFit = { ok: true, small: false };
    let cardFit = { ok: true };
    let destroyed = false;

    /* Worker session (old js:290-347) */
    let sessionLineId = newLineId();
    let photoToken = randHex(16);
    let uploadPromise = null;
    let uploadedForFile = null;     // the source (File/Blob, or photoCanvas) whose original is on the Worker under sessionLineId
    let uploadedFallback = null;    // { reason, w, h } when that original is a JPEG of photoCanvas (see uploadOriginal)
    /* 1.3.1: where the accepted photo came from — { source: 'studio', style, id, mode, result_url, print_url, w, h }
       for a DAILE STUDIO artwork (production upscales art differently: the photo upscaler flattens paper and
       brush texture), null for the customer's own photo. Written to _design.photo.origin. */
    let photoOrigin = null;
    function studioSizeMatch(w, h, o) {
      if (!o || !o.w || !o.h) return false;
      const k = w / o.w;
      return Math.abs(k - Math.round(k)) < 0.01 && Math.round(k) >= 1 && Math.round(k) <= 4 && Math.abs(h / o.h - k) < 0.01;
    }
    let uploadAbort = null;
    let uploadProgress = null;      // 0..1 while the original is being sent, for the "Adding… 42%" label
    let atcBusy = false;
    let submission = { attempted: false, confirmed: false, items: null };
    let lockedControls = null;

    const photoUrl = (kind) => WORKER + '/photo?line=' + sessionLineId + '&kind=' + kind + '&token=' + photoToken;
    const uploadUrl = (kind) => WORKER + '/upload?line=' + sessionLineId + '&kind=' + kind + '&token=' + photoToken;
    const photoSource = () => currentFile || photoCanvas;
    /** Long side for re-encoding the original at ≤ 16 MP (iOS canvas limit ≈ 16.7 MP). */
    const printSide = () => {
      const ph = store.getState().photo;
      if (!ph || !ph.width || !ph.height) return 4096;
      return Math.floor(Math.max(ph.width, ph.height) * Math.min(1, Math.sqrt(16e6 / (ph.width * ph.height))));
    };

    function abortUpload() {
      if (uploadAbort) { try { uploadAbort.abort(); } catch (e) { /* ok */ } uploadAbort = null; }
      uploadPromise = null;
      uploadProgress = null;
    }
    function resetUploaded() {
      uploadedForFile = null;
      uploadedFallback = null;
    }

    /** The Worker answered 409 "owned by another session" (this line id belongs to another
       token): continue on a fresh line id + token. Only before a cart/add was attempted — after
       that the line in the cart must keep its id (reconcileSubmission). */
    function rotateSession(why) {
      console.warn('[dv-customizer] upload line ' + sessionLineId + ' refused (' + why + '), continuing on a new line');
      sessionLineId = newLineId();
      photoToken = randHex(16);
      resetUploaded();
    }

    function busyLabel() {
      const base = t('js_adding', 'Adding…');
      return uploadProgress != null && uploadProgress < 1 ? base + ' ' + Math.floor(uploadProgress * 100) + '%' : base;
    }
    function showUploadProgress(f) {
      uploadProgress = f;
      if (!atcBusy) return;
      const text = busyLabel();
      els.nextBtns.forEach((btn) => {
        const label = btn.querySelector('[data-dv-cz-next-label]');
        if (label && label.textContent !== text) label.textContent = text;
      });
    }

    /** Original photo -> Worker (background from PHOTO -> Continue, joined by Add to cart).
       Source: the accepted photo (an in-memory copy when it was small enough). If there is no
       readable file while a photo is shown (no currentFile; or iOS "Load failed" /
       NotReadableError on the File), a JPEG of photoCanvas is sent instead, so the order can
       still be placed. When the Worker refuses the file itself (413 / 415) while it is still
       readable, the original is decoded again and sent as a JPEG of up to 16 MP.
       Trade-off: photoCanvas is the largest copy kept in memory, but it is the prepared copy
       (DVCZ.preparePhoto: 2000 px long side on phones, 2400 on desktop), not the original
       resolution; keeping a print-size copy as well would double the memory on phones. The
       design records any fallback (photo.original_fallback: reason, w, h) so production can
       ask the customer for the full file when the print size needs it. */
    function uploadOriginal() {
      const src = photoSource();
      if (!WORKER || !src) return Promise.resolve(false);
      if (uploadedForFile === src) return Promise.resolve(true);
      if (uploadPromise && uploadPromise.dvFile === src) return uploadPromise;
      abortUpload();
      const file = currentFile;
      const canvas = photoCanvas;
      const ac = 'AbortController' in window ? new AbortController() : null;
      uploadAbort = ac;
      let fallback = null;          // why a re-encoded JPEG goes instead of the file
      let fbCanvas = null;          // the canvas that JPEG is made from
      const useCanvas = (reason, cv) => {
        fallback = reason;
        fbCanvas = cv;
        console.warn('[dv-customizer] original: sending a JPEG of ' + cv.width + '×' + cv.height + ' px instead (' + reason + ')');
      };
      if (!file) useCanvas('no-file', canvas);
      const nextBody = (prev) => {
        let step = Promise.resolve();
        const refused = !!prev && (prev.status === 413 || prev.status === 415);
        if (refused && !fallback) {
          // Refused as it is, but still readable: the original again, decoded and re-encoded at ≤ 16 MP.
          step = blobReadable(file).then((readable) => (readable
            ? window.DVCZ.preparePhoto(file, printSide()).then((cv) => useCanvas('http-' + prev.status, cv), () => useCanvas('http-' + prev.status, canvas))
            : useCanvas('unreadable', canvas)));
        } else if (refused && fbCanvas !== canvas) {
          useCanvas('http-' + prev.status + '-again', canvas);
        } else if (!fallback && canvas && prev && prev.status === 0) {
          step = blobReadable(file).then((readable) => { if (!readable) useCanvas('unreadable', canvas); });
        }
        return step.then(() => (fallback
          ? canvasJpeg(fbCanvas).then((blob) => ({ data: blob, type: 'image/jpeg' }))
          : { data: file, type: file.type }));
      };
      const run = (rotations) => {
        showUploadProgress(0);
        return putWithRetry('original', uploadUrl('original'), nextBody, {
          signal: ac ? ac.signal : null,
          capMs: originalCap(file || { size: 0 }),
          onProgress: (f) => { if (uploadPromise === p) showUploadProgress(f); },
          retryStatus: (s) => (s === 413 || s === 415) && !!canvas && fbCanvas !== canvas,
        }).catch((e) => {
          if (e.conflict && rotations < 2 && !submission.attempted && uploadPromise === p) {
            rotateSession('409 on original');
            return run(rotations + 1);
          }
          throw e;
        });
      };
      const p = run(0).then(() => {
        if (uploadPromise === p) { uploadPromise = null; uploadProgress = null; }
        if (uploadAbort === ac) uploadAbort = null;
        if (photoSource() !== src) return true; // the photo changed meanwhile: nothing to record
        uploadedForFile = src;
        uploadedFallback = fallback && fbCanvas ? { reason: fallback, w: fbCanvas.width, h: fbCanvas.height } : null;
        return true;
      }, (error) => {
        if (uploadPromise === p) { uploadPromise = null; uploadProgress = null; }
        if (uploadAbort === ac) uploadAbort = null;
        throw error;
      });
      p.dvFile = src;
      uploadPromise = p;
      return p;
    }

    function uploadPreview(thumb) {
      if (!WORKER || !thumb) return Promise.resolve(false);
      return fetch(thumb).then((r) => r.blob())
        .then((blob) => putWithRetry('preview', uploadUrl('preview'), () => ({ data: blob, type: 'image/jpeg' }), { tries: 2, capMs: UPLOAD_TIMEOUT_MS, stallMs: 15000 }))
        .then(() => true, (e) => {
          console.warn('[dv-customizer] preview upload', e);
          return false; // best effort, like before
        });
    }

    /* ------------------------------------------------------------ gates */

    function maxAllowedStep(st) {
      if (!selectable(st.set)) return 0;
      if (!st.photo || cropQuality(st) === 'blocked') return 1;
      if (st.nfc.enabled && !validNfcUrl(st.nfc.url)) return 2;
      if (!lidFit.ok) return 2;
      if (st.card.added && !cardFit.ok) return 2; // review fix: an overflowing card message was not gated
      return STEPS.length - 1;
    }

    /* Which gift tab holds the reason the Review is locked (lid text and NFC
       live on the lid tab, the card message on the card tab). */
    const giftGateTab = (st) => ((st.nfc.enabled && !validNfcUrl(st.nfc.url)) || !lidFit.ok ? 'box' : 'card');

    function designResetPatch() {
      return {
        box: defaultBox(),
        nfc: { enabled: false, url: '' },
        card: defaultCard(),
        addons: { glue: false },
        extras: { bag: false, magnets: false, frameInterest: false },
        maxStepReached: 1,
        step: 1,
      };
    }

    /* --------------------------------------------------------- scenes */

    function puzzleScene(st) {
      return window.DVCZ.sceneFor({ set: st.set, orientation: orientName(st.orientation), kind: 'puzzle' });
    }
    function withBoxScene(st) {
      return window.DVCZ.sceneFor({ set: st.set, orientation: orientName(st.orientation), kind: 'with_box' });
    }
    function lidScene(st) {
      return window.DVCZ.getScene(isMini(st.set) ? 'mini-top-lid' : 'classic-box-top');
    }

    /* Old lid compositions -> package layouts (CONTRACT §10.5):
       CLASSIC default -> {o}-logo; no-daile -> {o}-no-logo (+ no-logo child line).
       MINI has no logo: default = photo + text.
       photo-only -> {o}-photo-centered on both (owner decision 2026-09-28): the photo
       centred on the lid, no logo, no text. Not in the package: generated by
       build-cz-assets.py in every lid scene (lid step and with-box Review). A manifest
       without it falls back to the old mapping ({o}-no-logo / {o} without text). */
    function lidLayoutId(st) {
      const o = orientName(st.orientation);
      if (st.box.variant === 'photo-only') {
        const centred = o + '-photo-centered';
        if ((lidScene(st).layouts || []).some((l) => l.id === centred)) return centred;
      }
      if (isMini(st.set)) return o;
      return st.box.variant === 'default' ? o + '-logo' : o + '-no-logo';
    }
    const lidMessage = (st) => (st.box.variant === 'photo-only' ? '' : String(st.box.text || '').trim());
    /* Lid example (owner 2026-09-30): while the lid text field is empty, the same
       example as its placeholder stands on the lid preview as a light "ghost",
       like the card sample. Lid step only (the Review shows the real box), never
       in the config or the cart (lidMessage above stays ''), and only when it
       fits the text slot of the chosen layout / font / size. */
    function lidGhost(st) {
      if (st.box.variant === 'photo-only' || st.box.text || !lidExample()) return '';
      try {
        const resolved = window.DVCZ.resolveLayout(lidScene(st), lidLayoutId(st));
        const slot = resolved.text_slots && resolved.text_slots.message;
        if (!slot || !slot.style) return '';
        return window.DVCZ.fitText(slot, lidExample(), lidTextOpts(st)).ok ? lidExample() : '';
      } catch (e) { return ''; }
    }
    const lidExample = () => String((els.lidText && els.lidText.getAttribute('placeholder')) || '').trim();
    /* The ghost keeps the chosen colour, lighter: silver / black at half strength. */
    function ghostColor(hex) {
      const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
      if (!m) return 'rgba(170, 167, 163, 0.5)';
      const n = parseInt(m[1], 16);
      return 'rgba(' + (n >> 16) + ', ' + ((n >> 8) & 255) + ', ' + (n & 255) + ', ' + (n > 0x808080 ? 0.6 : 0.36) + ')';
    }
    /* Flat-lid rects of a layout for the config snapshot: LID_RECTS for the package
       layouts; a generated layout ({o}-photo-centered) carries its own photo_rect in
       the manifest (one source for preview and print) and has no text field. */
    function lidRectsOf(st, layoutId) {
      const known = (LID_RECTS[SETS[st.set].box] || {})[layoutId];
      if (known) return known;
      const layout = (lidScene(st).layouts || []).find((l) => l.id === layoutId);
      return layout && layout.photo_rect ? { photo: layout.photo_rect, text: null } : null;
    }
    function lidTextOpts(st) {
      const f = faceFor(st.box.font, lidMessage(st) || lidExample()); // 1.3.6: Cyrillic → close face (ghost example too)
      return { font_family: f.family, font_weight: f.weight, color: st.box.color || SILVER, size_factor: LID_SIZE_FACTORS[st.box.sizePreset] || 1, size_max_factor: LID_SIZE_CAPS[st.box.sizePreset] || 1 };
    }
    function cardTextOpts(st) {
      if (!st.card.added) return { font_family: FONTS.cormorant.family, font_weight: FONTS.cormorant.weight, color: CARD_GHOST, size_factor: 1, align: 'justify' };
      const f = faceFor(st.card.font, cardMessage(st)); // 1.3.6
      return { font_family: f.family, font_weight: f.weight, color: st.card.color || BLACK, size_factor: SIZE_FACTORS[st.card.sizePreset] || 1, align: st.card.align || 'justify' };
    }
    const cardMessage = (st) => (st.card.added ? String(st.card.text || '') : CARD_SAMPLE);
    const cardOrient = (st) => (st.card.orient === 'v' || st.card.orient === 'h' ? st.card.orient : (st.orientation === 'h' ? 'h' : 'v'));
    const cardSlotOf = (st) => (cardOrient(st) === 'h' && cardSlotH ? cardSlotH : cardSlot);
    const cardSceneId = (st) => (st.card.added && !String(st.card.text || '').trim() ? 'postcard-photo' : 'postcard-photo-and-text')
      + (cardOrient(st) === 'h' && cardSceneH ? '-h' : '');
    const lidSlotOf = (resolved) => (resolved.slots || []).find((s) => s.role === 'lid') || (resolved.slots || [])[0];

    /* The card sits upright: a landscape photo lies rotated 90° clockwise on it,
       exactly like the old ".daile-cz__card-rot" (old css:626-634). */
    let rotated = { key: '', canvas: null };
    function cardPhoto(st, slot) {
      const pw = photoCanvas.width;
      const ph = photoCanvas.height;
      // 1.3.3: a landscape card takes the photo as it is (a portrait photo is cropped to the card, never turned)
      if (cardOrient(st) === 'h') return { photo: photoCanvas, rect: cropRect(pw, ph, slot.aspect, st.crop) };
      if (st.orientation !== 'h') {
        return { photo: photoCanvas, rect: cropRect(pw, ph, slot.aspect, st.crop) };
      }
      const r = cropRect(pw, ph, 1 / slot.aspect, st.crop);
      const key = photoVersion + ':' + r.join(',');
      if (rotated.key !== key) {
        const sw = r[2] * pw;
        const sh = r[3] * ph;
        const k = Math.min(1, 1600 / Math.max(sw, sh));
        const c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(sh * k));
        c.height = Math.max(1, Math.round(sw * k));
        const ctx = c.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.translate(c.width, 0);
        ctx.rotate(Math.PI / 2);
        ctx.drawImage(photoCanvas, r[0] * pw, r[1] * ph, sw, sh, 0, 0, c.height, c.width);
        rotated = { key, canvas: c };
      }
      return { photo: rotated.canvas, rect: [0, 0, 1, 1] };
    }

    let samplePromise = null;
    function loadSample() {
      if (!samplePromise) {
        samplePromise = window.DVCZ.loadImage(manifest.sample_image).catch((e) => {
          samplePromise = null;
          throw e;
        });
      }
      return samplePromise;
    }

    /** What a stage shows for a given state; key changes => re-render. */
    function specFor(name, st) {
      if (!st.set) return null;
      if (name === 'upload') {
        const scene = puzzleScene(st);
        const slot = scene.slots[0];
        if (!photoCanvas) return { scene, key: [scene.id, 'sample'], photo: loadSample, opts: {} };
        const rect = cropRect(photoCanvas.width, photoCanvas.height, slot.aspect, st.crop, bleedFor(st.set, st.orientation));
        return { scene, key: [scene.id, photoVersion, rect], photo: () => photoCanvas, opts: { cropBySlot: { [slot.id]: rect } } };
      }
      if (!photoCanvas) return null;
      if (name === 'lid') {
        const scene = lidScene(st);
        const layoutId = lidLayoutId(st);
        const slot = lidSlotOf(window.DVCZ.resolveLayout(scene, layoutId));
        const rect = cropRect(photoCanvas.width, photoCanvas.height, slot.aspect, st.crop);
        const message = lidFit.ok ? lidMessage(st) : '';
        const text = lidTextOpts(st);
        const ghost = message ? '' : lidGhost(st);
        const drawn = ghost ? { ...text, color: ghostColor(text.color) } : text;
        return { scene, key: [scene.id, layoutId, photoVersion, rect, message, text, ghost], photo: () => photoCanvas, opts: { layoutId, message: message || ghost, text: drawn, cropBySlot: { [slot.id]: rect } } };
      }
      if (name === 'card') {
        const scene = window.DVCZ.getScene(cardSceneId(st));
        const slot = scene.slots[0];
        const cp = cardPhoto(st, slot);
        const message = cardFit.ok ? cardMessage(st) : '';
        const text = cardTextOpts(st);
        return { scene, key: [scene.id, photoVersion, st.orientation, cardOrient(st), cp.rect, rotated.key, message, text], photo: () => cp.photo, opts: { message, text, cropBySlot: { [slot.id]: cp.rect } } };
      }
      if (name === 'review') {
        const scene = withBoxScene(st);
        const layoutId = lidLayoutId(st);
        const resolved = window.DVCZ.resolveLayout(scene, layoutId);
        const crops = {};
        (resolved.slots || []).forEach((slot) => {
          crops[slot.id] = slot.role === 'puzzle'
            ? cropRect(photoCanvas.width, photoCanvas.height, slot.aspect, st.crop, bleedFor(st.set, st.orientation))
            : cropRect(photoCanvas.width, photoCanvas.height, slot.aspect, st.crop);
        });
        const message = lidFit.ok ? lidMessage(st) : '';
        const text = lidTextOpts(st);
        return { scene, key: [scene.id, layoutId, photoVersion, crops, message, text], photo: () => photoCanvas, opts: { layoutId, message, text, cropBySlot: crops } };
      }
      return null;
    }

    /* --------------------------------------------------------- stages */

    function makeView(name) {
      const stage = $('[data-dv-cz-stage="' + name + '"]');
      return { name, stage, canvas: stage ? stage.querySelector('[data-dv-cz-canvas]') : null, off: null, key: '', pendingKey: '', ac: null, timer: 0, raf: 0, busyT: 0, retryT: 0, failures: 0, failKey: '' };
    }
    const views = { upload: makeView('upload'), lid: makeView('lid'), card: makeView('card'), review: makeView('review') };

    function renderWidth(stage) {
      const w = stage && stage.clientWidth;
      if (!w) return 0;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      return Math.max(300, Math.min(MAX_RENDER_WIDTH, Math.ceil((w * dpr) / 100) * 100));
    }
    /* Render width guess while every stage is hidden (set step warm-up): one
       column edge to edge up to 900 px, else about half the container and at
       most the screen height (css "Preview stage"). Only picks the 800 px or
       1600 px layer files to download. */
    function expectedRenderWidth() {
      const vw = root.clientWidth || window.innerWidth || 0;
      const css = vw <= 900 ? vw : Math.min(vw * 0.5, (window.innerHeight || vw) - 120);
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      return Math.max(300, Math.min(MAX_RENDER_WIDTH, Math.ceil((css * dpr) / 100) * 100));
    }

    function visibleViews(st) {
      const step = STEPS[st.step];
      if (step === 'upload') return ['upload'];
      if (step === 'gift') return [giftTab === 'card' ? 'card' : 'lid'];
      if (step === 'review') return ['review'];
      return [];
    }

    function requestRender(view, delay) {
      if (!view.stage || destroyed) return;
      window.clearTimeout(view.timer);
      view.timer = window.setTimeout(() => {
        window.cancelAnimationFrame(view.raf);
        view.raf = window.requestAnimationFrame(() => { renderView(view); });
      }, delay || 0);
    }

    /* Polite screen-reader status (visual polish pass): photo added, preview
       ready after a real scene change (not on every keystroke), saving. */
    let liveTimer = 0;
    let previewSig = '';
    let previewQuietUntil = 0;
    function announce(msg) {
      if (!els.live || !msg) return;
      window.clearTimeout(liveTimer);
      els.live.textContent = '';
      liveTimer = window.setTimeout(() => { if (!destroyed) els.live.textContent = msg; }, 80);
    }
    function announcePreview(view, spec) {
      if (!photoCanvas) return; // the sample scene on arrival is not news
      const layout = view.name === 'lid' || view.name === 'review' ? spec.key[1] : '';
      const sig = [view.name, spec.scene && spec.scene.id, layout, photoVersion].join('|');
      if (sig === previewSig) return;
      previewSig = sig;
      if (Date.now() < previewQuietUntil) return;
      announce(t('dv_cz_live_preview', 'Preview updated.'));
    }

    let renderErrorShown = false;
    function showRenderError(showIt) {
      if (!showIt) { renderErrorShown = false; return; }
      if (renderErrorShown) return;
      renderErrorShown = true;
      const msg = t('dv_cz_js_render_error', 'The preview could not be drawn. Please try again.');
      if (window.DV && typeof window.DV.toast === 'function') window.DV.toast(msg, { timeout: 5000 });
    }

    async function renderView(view) {
      const st = store.getState();
      const spec = specFor(view.name, st);
      if (!spec) return;
      const width = renderWidth(view.stage);
      if (!width) return; // panel hidden: rendered when it becomes visible
      const key = JSON.stringify([spec.key, width]);
      if (key === view.key) {
        if (view.name === 'upload' && !gesturing && els.proxy) els.proxy.hidden = true; // gesture ended without a change
        return;
      }
      if (key === view.pendingKey) return;
      if (view.ac) view.ac.abort();
      const ac = new AbortController();
      view.ac = ac;
      view.pendingKey = key;
      window.clearTimeout(view.retryT);
      /* The busy hairline shows only after 350 ms, so quick redraws never
         flicker. A JS timer, not a CSS transition-delay: reduced motion turns
         every transition off in dv-base, and the delay went with it. */
      window.clearTimeout(view.busyT);
      view.busyT = window.setTimeout(() => { if (view.ac === ac) view.stage.classList.add('is-busy'); }, 350);
      view.stage.setAttribute('aria-busy', 'true');
      try {
        const photo = await spec.photo();
        if (ac.signal.aborted) return;
        if (!view.off) view.off = document.createElement('canvas');
        const out = await window.DVCZ.renderScene(spec.scene, photo, { ...spec.opts, width, canvas: view.off, signal: ac.signal });
        if (ac.signal.aborted) return;
        const canvas = view.canvas;
        if (canvas.width !== out.width) canvas.width = out.width;
        if (canvas.height !== out.height) canvas.height = out.height;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(out, 0, 0);
        view.key = key;
        view.info = out.dvcz;
        view.failures = 0;
        view.stage.classList.remove('is-loading');
        showRenderError(false);
        announcePreview(view, spec);
        afterRender(view);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        console.error('[dv-customizer] render ' + view.name, error);
        showRenderError(true);
        /* Review fix: a failed layer download left "Preparing preview…" up for
           good. Retry a few times with a growing pause (the renderer drops the
           failed download from its cache); an "online" event retries as well. */
        if (view.failKey !== key) { view.failKey = key; view.failures = 0; }
        view.failures += 1;
        if (view.failures <= 3) {
          window.clearTimeout(view.retryT);
          view.retryT = window.setTimeout(() => requestRender(view), 1500 * view.failures);
        }
      } finally {
        if (view.ac === ac) {
          view.ac = null;
          view.pendingKey = '';
          window.clearTimeout(view.busyT);
          view.stage.classList.remove('is-busy');
          if (!(view.name === 'upload' && photoProcessing)) view.stage.removeAttribute('aria-busy');
        }
      }
    }

    function afterRender(view) {
      if (view.name === 'upload') {
        if (!gesturing && els.proxy) els.proxy.hidden = true;
        preloadNext(['lid', 'review']);
      } else if (view.name === 'lid') {
        preloadNext(['card', 'review']);
      }
    }

    function preloadNext(names) {
      idle(() => {
        if (destroyed) return;
        const st = store.getState();
        if (!st.set || !photoCanvas) return;
        const width = renderWidth(views.upload.stage) || renderWidth(views.lid.stage) || renderWidth(views.review.stage) || 800;
        names.forEach((name) => {
          let scene = null;
          if (name === 'lid') scene = lidScene(st);
          else if (name === 'review') scene = withBoxScene(st);
          else if (name === 'card') scene = window.DVCZ.getScene(cardSceneId(st));
          if (scene) window.DVCZ.preload(scene, { width }).catch(() => { /* rendered later anyway */ });
        });
      });
    }

    function scheduleVisible(delay) {
      visibleViews(store.getState()).forEach((name) => requestRender(views[name], delay));
    }

    /* ------------------------------------------ upload zone + gestures */

    let zoneBox = null; // slot bounding box in stage fractions
    function positionZone(st) {
      if (!els.zone) return;
      if (!st.set) return;
      const scene = puzzleScene(st);
      const q = scene.slots[0].quad;
      const [cw, ch] = scene.canvas_size || manifest.canvas_size;
      const xs = q.map((p) => p[0]);
      const ys = q.map((p) => p[1]);
      const box = { x: Math.min(...xs) / cw, y: Math.min(...ys) / ch, w: (Math.max(...xs) - Math.min(...xs)) / cw, h: (Math.max(...ys) - Math.min(...ys)) / ch };
      zoneBox = box;
      const z = els.zone.style;
      z.left = (box.x * 100) + '%';
      z.top = (box.y * 100) + '%';
      z.width = (box.w * 100) + '%';
      z.height = (box.h * 100) + '%';
      const relief = (scene.overlays || []).find((o) => o.role === 'puzzle-cut-relief');
      // Same layer set as the canvas: the 800 px relief when the preview renders at <= 800 px.
      const w = renderWidth(views.upload.stage) || expectedRenderWidth();
      const reliefUrl = relief && ((relief.path_small && w <= ((scene.asset_size_small || [0])[0] || 0)) ? relief.path_small : relief.path);
      if (els.proxyRelief && relief && els.proxyRelief.getAttribute('data-src') !== reliefUrl) {
        els.proxyRelief.setAttribute('data-src', reliefUrl);
        els.proxyRelief.src = reliefUrl;
      }
    }

    const live = { zoom: 1, offsetX: 0, offsetY: 0 };
    let gesturing = false;

    function layoutProxy() {
      const st = store.getState();
      if (!els.zone || !els.proxyPhoto || !photoCanvas || !st.set) return;
      const zw = els.zone.clientWidth;
      const zh = els.zone.clientHeight;
      if (!zw || !zh) return;
      const bleed = bleedFor(st.set, st.orientation);
      let zoom; let ox; let oy;
      if (gesturing) { zoom = live.zoom; ox = live.offsetX; oy = live.offsetY; } else {
        const c = clampCrop(photoCanvas.width, photoCanvas.height, zw / zh, st.crop, bleed);
        zoom = c.zoom; ox = c.nx * zw; oy = c.ny * zh;
      }
      const needW = zw * (1 + bleed.fx);
      const needH = zh * (1 + bleed.fy);
      const scale = Math.max(needW / photoCanvas.width, needH / photoCanvas.height) * zoom;
      const dw = photoCanvas.width * scale;
      const dh = photoCanvas.height * scale;
      const maxX = Math.max(0, (dw - needW) / 2);
      const maxY = Math.max(0, (dh - needH) / 2);
      ox = clamp(ox, -maxX, maxX);
      oy = clamp(oy, -maxY, maxY);
      if (gesturing) { live.offsetX = ox; live.offsetY = oy; }
      const s = els.proxyPhoto.style;
      s.width = dw + 'px';
      s.height = dh + 'px';
      s.left = ((zw - dw) / 2 + ox) + 'px';
      s.top = ((zh - dh) / 2 + oy) + 'px';
      if (els.proxyRelief && zoneBox) {
        const stage = views.upload.stage;
        const r = els.proxyRelief.style;
        r.width = stage.clientWidth + 'px';
        r.height = stage.clientHeight + 'px';
        r.left = (-zoneBox.x * stage.clientWidth) + 'px';
        r.top = (-zoneBox.y * stage.clientHeight) + 'px';
      }
    }

    function beginGesture() {
      if (gesturing || !els.zone) return;
      const st = store.getState();
      const zw = els.zone.clientWidth;
      const zh = els.zone.clientHeight;
      const c = clampCrop(photoCanvas.width, photoCanvas.height, zw / zh || 1, st.crop, bleedFor(st.set, st.orientation));
      live.zoom = c.zoom;
      live.offsetX = c.nx * zw;
      live.offsetY = c.ny * zh;
      gesturing = true;
      if (els.proxy) els.proxy.hidden = false;
      layoutProxy();
    }

    function commitCrop() {
      const zw = els.zone.clientWidth;
      const zh = els.zone.clientHeight;
      gesturing = false;
      if (!zw || !zh) return;
      const st = store.getState();
      const slot = puzzleScene(st).slots[0];
      const next = clampCrop(photoCanvas.width, photoCanvas.height, slot.aspect, { zoom: live.zoom, nx: live.offsetX / zw, ny: live.offsetY / zh }, bleedFor(st.set, st.orientation));
      store.set({ crop: { zoom: round(next.zoom, 4), nx: round(next.nx, 5), ny: round(next.ny, 5) } });
    }

    function dismissGestureHint() {
      if (gestureHintDone) return;
      gestureHintDone = true;
      if (!els.gestureHint) return;
      els.gestureHint.classList.add('is-fading');
      window.setTimeout(() => { els.gestureHint.hidden = true; }, 450);
    }

    function initGestures() {
      const zone = els.zone;
      if (!zone || !window.PointerEvent) return;
      const pointers = new Map();
      let startDist = 0;
      let startZoom = 1;
      const distance = () => {
        const [a, b] = Array.from(pointers.values());
        return Math.hypot(a.x - b.x, a.y - b.y);
      };
      on(zone, 'pointerdown', (e) => {
        if (!store.getState().photo || !photoCanvas) return;
        if (e.target.closest('[data-dv-cz-browse]')) return;
        dismissGestureHint();
        beginGesture();
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size === 2) { startDist = distance(); startZoom = live.zoom; }
        try { zone.setPointerCapture(e.pointerId); } catch (err) { /* iOS < 13 */ }
        e.preventDefault();
      });
      on(zone, 'pointermove', (e) => {
        const p = pointers.get(e.pointerId);
        if (!p) return;
        if (pointers.size === 1) {
          live.offsetX += e.clientX - p.x;
          live.offsetY += e.clientY - p.y;
        }
        p.x = e.clientX;
        p.y = e.clientY;
        if (pointers.size === 2 && startDist > 0) live.zoom = clamp(startZoom * (distance() / startDist), 1, 3);
        layoutProxy();
      });
      const release = (e) => {
        if (!pointers.has(e.pointerId)) return;
        pointers.delete(e.pointerId);
        if (pointers.size < 2) startDist = 0;
        if (!pointers.size && gesturing) commitCrop();
      };
      on(zone, 'pointerup', release);
      on(zone, 'pointercancel', release);
      // Keyboard alternative to the gestures (old gap: no keyboard crop).
      on(zone, 'keydown', (e) => {
        if (!store.getState().photo || !photoCanvas) return;
        const step = e.shiftKey ? 0.1 : 0.02;
        let move = null;
        if (e.key === 'ArrowLeft') move = [-step, 0, 0];
        else if (e.key === 'ArrowRight') move = [step, 0, 0];
        else if (e.key === 'ArrowUp') move = [0, -step, 0];
        else if (e.key === 'ArrowDown') move = [0, step, 0];
        else if (e.key === '+' || e.key === '=') move = [0, 0, 0.1];
        else if (e.key === '-' || e.key === '_') move = [0, 0, -0.1];
        if (!move) return;
        e.preventDefault();
        nudgeCrop(move[0], move[1], move[2]);
      });
    }

    /** Moves / zooms the committed crop (arrow and +/- keys on the photo zone;
     *  the move buttons were removed by the owner 2026-09-30).
     *  dx, dy: fractions of the zone (+x = photo to the right); dz: zoom step. */
    function nudgeCrop(dx, dy, dz) {
      const st = store.getState();
      if (!st.photo || !photoCanvas) return;
      dismissGestureHint();
      const slot = puzzleScene(st).slots[0];
      const next = { zoom: st.crop.zoom + (dz || 0), nx: st.crop.nx + (dx || 0), ny: st.crop.ny + (dy || 0) };
      const c = clampCrop(photoCanvas.width, photoCanvas.height, slot.aspect, next, bleedFor(st.set, st.orientation));
      store.set({ crop: { zoom: round(c.zoom, 4), nx: round(c.nx, 5), ny: round(c.ny, 5) } });
    }

    /* ------------------------------------------------------ file intake */

    function showUploadError(msg) {
      if (!els.uploadError) return;
      els.uploadError.textContent = msg || '';
      els.uploadError.hidden = !msg;
    }

    function setProxySource() {
      if (proxyUrl) { URL.revokeObjectURL(proxyUrl); proxyUrl = null; }
      if (!photoCanvas || !els.proxyPhoto) return;
      const version = photoVersion;
      photoCanvas.toBlob((blob) => {
        if (!blob || version !== photoVersion || destroyed) return;
        proxyUrl = URL.createObjectURL(blob);
        els.proxyPhoto.src = proxyUrl;
        els.proxyPhoto.width = photoCanvas.width;
        els.proxyPhoto.height = photoCanvas.height;
      }, 'image/jpeg', 0.85);
    }

    /* keepDesign: edit mode — crop, orientation and the design are already in state.
       userInitiated: picked or dropped by the customer (not the edit / PDP boot):
       focus then moves to the photo zone, which also scrolls the new crop into
       view on phones (review fix: focus fell to <body> when the chooser hid). */
    let processingT = 0;
    let photoProcessing = false;
    function acceptFile(picked, keepDesign, userInitiated, origin) {
      if (!picked) return;
      if (picked.type !== 'image/jpeg' && picked.type !== 'image/png') {
        showUploadError(t('js_err_format', 'Please upload a JPG or PNG photo.'));
        return;
      }
      if (picked.size > MAX_PHOTO_MB * 1024 * 1024) {
        showUploadError(t('dv_cz_js_err_big', 'This photo is over {mb} MB — please use a smaller file (JPG works best).', { mb: MAX_PHOTO_MB }));
        return;
      }
      const seq = ++uploadSeq;
      /* Visible + aria-busy state while the file is decoded and scaled (a large
         phone photo takes a moment); the redraw that follows keeps its own is-busy.
         The hairline waits 350 ms like the redraw one (a timer, see renderView). */
      const stage = views.upload.stage;
      const processing = (on) => {
        if (!stage || seq !== uploadSeq) return;
        window.clearTimeout(processingT);
        photoProcessing = on;
        if (on) {
          stage.setAttribute('aria-busy', 'true');
          // 1.3.2: picked by the customer → the «Preparing your photo…» state at once (was a 2 px line after 350 ms)
          if (userInitiated) stage.classList.add('is-processing');
          else processingT = window.setTimeout(() => { if (seq === uploadSeq) stage.classList.add('is-processing'); }, 350);
        } else {
          stage.classList.remove('is-processing');
          if (!views.upload.ac) stage.removeAttribute('aria-busy');
          pickReset();
        }
      };
      processing(true);
      // The photo is read into memory first (MEM_COPY_MAX_MB): the upload later reads this copy.
      memoryCopy(picked).then((file) => {
        if (seq !== uploadSeq || destroyed) return;
        const url = URL.createObjectURL(file);
        const probe = new Image();
        probe.onload = () => {
          if (seq !== uploadSeq || destroyed) { URL.revokeObjectURL(url); return; }
          const w = probe.naturalWidth;
          const h = probe.naturalHeight;
          const q = fileQuality(w, h);
          if (q === 'blocked') {
            URL.revokeObjectURL(url);
            processing(false);
            showUploadError(t('dv_cz_js_err_small', 'This photo is too small for print ({size} px). Please upload a larger one.', { size: w + '×' + h }));
            track('photo_quality_warning', { level: 'blocked', width: w, height: h });
            return;
          }
          const small = window.matchMedia && window.matchMedia('(max-width: 900px)').matches;
          window.DVCZ.preparePhoto(probe, small ? 2000 : 2400).then((canvas) => {
            URL.revokeObjectURL(url);
            if (seq !== uploadSeq || destroyed) return;
            processing(false);
            photoCanvas = canvas;
            photoVersion += 1;
            rotated = { key: '', canvas: null };
            currentFile = file;
            // 1.3.1: STUDIO hand-off only if the picture IS that artwork: its size is the artwork's ×1…×4 exactly
            // (STUDIO doubles small artworks; a print master is ×4) — a customer's own photo of similar
            // proportions does not match pixel for pixel
            photoOrigin = origin && studioSizeMatch(w, h, origin) ? origin : null;
            abortUpload();
            resetUploaded();
            showUploadError(null);
            setProxySource();
            let patch;
            if (keepDesign) {
              patch = { photo: { width: w, height: h, quality: q } };
            } else {
              const orientation = w > h ? 'h' : (h > w ? 'v' : store.getState().orientation);
              patch = { photo: { width: w, height: h, quality: q }, crop: { zoom: 1, nx: 0, ny: 0 }, orientation };
              if (pendingReset) {
                Object.assign(patch, designResetPatch());
                pendingReset = false;
              }
            }
            store.set(patch);
            if (keepDesign) {
              const target = selectable(store.getState().set) ? STEPS.length - 1 : 0;
              store.set({ maxStepReached: Math.max(store.getState().maxStepReached, target), step: target });
              if (target === STEPS.length - 1) markReview();
            } else if (userInitiated && els.zone && STEPS[store.getState().step] === 'upload') {
              // The chooser that had focus is hidden now; the zone (tabindex 0 with a photo) takes it.
              // No preventScroll: on phones this brings the new crop into view.
              try { els.zone.focus(); } catch (e) { /* ignore */ }
            }
            const set = store.getState().set || '';
            if (!keepDesign) {
              previewQuietUntil = Date.now() + 2500; // the photo message covers the first redraw
              announce(t('dv_cz_live_photo', 'Photo added.') + ' ' + ((els.quality && els.quality.textContent) || ''));
            }
            track('photo_uploaded', { width: w, height: h, quality: q, set });
            if (q !== 'green') track('photo_quality_warning', { level: q, width: w, height: h });
          }).catch((e) => {
            URL.revokeObjectURL(url);
            console.error('[dv-customizer] prepare photo', e);
            processing(false);
            if (seq === uploadSeq) showUploadError(t('js_err_read', 'We could not read this file. Please try another photo.'));
          });
        };
        probe.onerror = () => {
          URL.revokeObjectURL(url);
          if (seq !== uploadSeq) return;
          processing(false);
          showUploadError(t('js_err_read', 'We could not read this file. Please try another photo.'));
        };
        probe.src = url;
      });
    }

    function removePhoto() {
      uploadSeq += 1;
      window.clearTimeout(processingT);
      photoProcessing = false;
      if (views.upload.stage) views.upload.stage.classList.remove('is-processing'); // a file still being read is dropped
      photoCanvas = null;
      photoVersion += 1;
      rotated = { key: '', canvas: null };
      if (proxyUrl) { URL.revokeObjectURL(proxyUrl); proxyUrl = null; }
      if (els.proxyPhoto) els.proxyPhoto.removeAttribute('src');
      currentFile = null;
      abortUpload();
      resetUploaded();
      if (els.file) els.file.value = '';
      gesturing = false;
      showUploadError(null);
      store.set({ photo: null, crop: { zoom: 1, nx: 0, ny: 0 } });
    }

    const designStarted = () => store.getState().photo && store.getState().maxStepReached > 1;

    function openDialog(dialog, trigger) {
      if (!dialog) return;
      if (window.DV && window.DV.openDialog) window.DV.openDialog(dialog, trigger);
      else if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
      const video = dialog.querySelector('video');
      if (video) {
        if (!video.getAttribute('src') && video.getAttribute('data-src')) video.setAttribute('src', video.getAttribute('data-src'));
        if (!reducedMotion.matches) { try { const pr = video.play(); if (pr && pr.catch) pr.catch(() => {}); } catch (e) { /* autoplay blocked */ } } else video.controls = true;
      }
    }
    function closeDialog(dialog) {
      if (!dialog) return;
      if (window.DV && window.DV.closeDialog) window.DV.closeDialog(dialog);
      else if (dialog.open) dialog.close();
    }

    /* Focus return (review fix): several controls hide themselves when used
       (Browse / Remove / Add glue / ×). Focus is moved to the control that
       takes their place, never left on <body>. */
    function focusQuiet(el) {
      if (!el || typeof el.focus !== 'function') return;
      try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
    }
    function focusBrowse() {
      focusQuiet(els.dropzone && els.dropzone.querySelector('[data-dv-cz-browse]'));
    }

    /* 1.3.2 (22.10, analytics: «+ ADD YOUR PHOTO» showed no reaction, people tapped again and again — iOS converts
       HEIC / fetches iCloud photos before 'change' with nothing on screen): the button is a native <label for> of the
       file input (no programmatic click — the fragile path in Instagram / TikTok WebViews), it says «Opening your
       photos…» at once, «Preparing your photo…» when the chooser closes, and ignores taps while a photo is prepared. */
    let pickT = 0;
    const pickLabel = (text) => { $$('[data-dv-cz-pick-label]').forEach((el) => { el.textContent = text; }); };
    function pickReset() {
      window.clearTimeout(pickT);
      if (views.upload && views.upload.stage) views.upload.stage.classList.remove('is-picking');
      if (!photoProcessing) pickLabel(t('cz_add_photo', '+ Add your photo'));
    }
    function pickStarted() {
      const stage = views.upload && views.upload.stage;
      if (stage) stage.classList.add('is-picking');
      pickLabel(t('cz_opening', 'Opening your photos…'));
      try { track('photo_picker_opened', { set: store.getState().set || '' }); } catch (e) { /* tracking is optional */ }
      window.clearTimeout(pickT);
      window.addEventListener('focus', () => {
        if (!stage || !stage.classList.contains('is-picking')) return;
        pickLabel(t('cz_preparing', 'Preparing your photo…'));
        window.clearTimeout(pickT);
        pickT = window.setTimeout(pickReset, 15000); // neither 'change' nor 'cancel' came: give the button back
      }, { once: true });
      pickT = window.setTimeout(pickReset, 60000);
    }

    function browse() {
      if (!els.file) return;
      if (designStarted()) {
        confirmPending = 'replace';
        openDialog(els.confirmDialog);
        return;
      }
      els.file.click();
    }

    function dropFile(file) {
      if (!file) return;
      if (designStarted()) { // old bug B11: a drop used to bypass the confirmation
        confirmPending = { file };
        openDialog(els.confirmDialog);
        return;
      }
      acceptFile(file, false, true);
    }

    /* ------------------------------------------------------------ nav */

    function markReview() {
      if (!reviewTracked) { reviewTracked = true; track('review_reached', { set: store.getState().set || '' }); }
    }

    function scrollToTop() {
      try { root.scrollIntoView({ block: 'start', behavior: reducedMotion.matches ? 'auto' : 'smooth' }); } catch (e) { /* old browsers */ }
    }

    /* After a user-initiated step change, move focus to the new panel heading,
       so keyboard and screen-reader users land at the start of the step. */
    function focusStep() {
      const st = store.getState();
      const panel = els.panels.find((p) => p.getAttribute('data-dv-cz-panel') === STEPS[st.step]);
      if (!panel) return;
      const sub = STEPS[st.step] === 'gift' ? panel.querySelector('[data-dv-cz-gift-sub="' + giftTab + '"]') : panel;
      const heading = (sub || panel).querySelector('h1, h2');
      if (!heading) return;
      heading.setAttribute('tabindex', '-1');
      try { heading.focus({ preventScroll: true }); } catch (e) { heading.focus(); }
    }

    function goTo(idx, opts = {}) {
      const st = store.getState();
      idx = Math.min(idx, maxAllowedStep(st));
      if (idx === st.step || idx < 0 || idx >= STEPS.length) return;
      if (STEPS[idx] === 'gift' && idx > st.step && !opts.keepTab) giftTab = 'box';
      if (STEPS[st.step] === 'upload' && idx > st.step && st.photo && photoSource()) {
        uploadOriginal().catch((e) => console.warn('[dv-customizer] background upload', e)); // old js:1514-1518
      }
      store.set({ step: idx, maxStepReached: Math.max(st.maxStepReached, idx) });
      if (idx === STEPS.length - 1) markReview();
      if (!opts.noScroll) { scrollToTop(); focusStep(); }
    }

    function setGiftTab(v, focus) {
      if (v !== 'box' && v !== 'card') return;
      if (v !== giftTab) {
        giftTab = v;
        render(store.getState());
      }
      if (focus) { const b = els.giftTabs.find((x) => x.getAttribute('data-dv-cz-gift-tab') === v); if (b) b.focus(); }
    }

    function chooseSet(key) {
      if (!SETS[key] || !selectable(key)) return;
      const st = store.getState();
      const patch = { set: key };
      if (isMini(key) && st.box.variant === 'no-daile') patch.box = { ...st.box, variant: 'default' };
      if (!isMini(key) && st.addons.glue) patch.addons = { ...st.addons, glue: false };
      if (st.set !== key) track('size_selected', { set: key, pieces: SETS[key].pieces });
      store.set(patch);
      if (els.setNotice) els.setNotice.hidden = true;
      const target = returnStep != null && returnStep > 0 ? returnStep : 1;
      returnStep = null;
      goTo(target);
    }

    /* ------------------------------------------------------------ render */

    const setPressed = (btn, on) => btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    const show = (el, on) => { if (el) el.hidden = !on; };

    function cardFor(key) {
      return els.setCards.find((c) => c.getAttribute('data-dv-cz-set') === key) || null;
    }

    function sizeLine(st) {
      const meta = SETS[st.set];
      const cm = st.orientation === 'h' ? [meta.cm[1], meta.cm[0]] : meta.cm;
      const inch = st.orientation === 'h' ? [meta.inch[1], meta.inch[0]] : meta.inch;
      return t('dv_cz_js_size_line', '{pieces} pieces · {cm} cm · {in} in', { pieces: fmtNum(meta.pieces), cm: iso(cm.join(' × ')), in: iso(inch.join(' × ')) });
    }
    function orientLine(st) {
      const meta = SETS[st.set];
      const cm = iso((st.orientation === 'h' ? [meta.cm[1], meta.cm[0]] : meta.cm).join(' × '));
      return st.orientation === 'h' ? t('dv_cz_js_orient_l', 'Landscape · {cm} cm', { cm }) : t('dv_cz_js_orient_p', 'Portrait · {cm} cm', { cm });
    }
    const setName = (key) => { const c = cardFor(key); return (c && c.getAttribute('data-name')) || SETS[key].title; };

    function refreshLidFit(st) {
      lidFit = { ok: true, small: false };
      const message = lidMessage(st);
      if (!st.set || !message) return;
      try {
        const resolved = window.DVCZ.resolveLayout(lidScene(st), lidLayoutId(st));
        const slot = resolved.text_slots && resolved.text_slots.message;
        if (!slot || !slot.style) return;
        const fit = window.DVCZ.fitText(slot, message, lidTextOpts(st));
        const q = slot.quad;
        const slotWidth = (Math.hypot(q[1][0] - q[0][0], q[1][1] - q[0][1]) + Math.hypot(q[2][0] - q[3][0], q[2][1] - q[3][1])) / 2;
        const units = (fit.fontPx / fit.texWidth) * slotWidth;
        lidFit = { ok: fit.ok, small: fit.ok && units < LID_TEXT_WARN_UNITS, fit, units };
      } catch (e) { /* measured again at render time */ }
    }

    function refreshCardFit(st) {
      cardFit = { ok: true };
      if (!st.card.added || !String(st.card.text || '').trim() || !cardSlotOf(st)) return;
      try {
        const fit = window.DVCZ.fitText(cardSlotOf(st), st.card.text, cardTextOpts(st));
        cardFit = { ok: fit.ok, fit };
      } catch (e) { /* ignore */ }
    }

    function render(st) {
      if (destroyed) return;
      const step = STEPS[st.step];
      refreshLidFit(st);
      refreshCardFit(st);

      root.classList.toggle('dv-cz--mini', isMini(st.set));
      root.classList.toggle('dv-cz--h', st.orientation === 'h');
      root.setAttribute('data-step', step);

      els.panels.forEach((p) => { p.hidden = p.getAttribute('data-dv-cz-panel') !== step; });
      show(els.builderHeading, st.step > 0);
      show(els.progress, st.step > 0 || !!st.set);
      show(els.chosen, st.step > 0 && !!st.set);
      show(els.previewBanner, st.step > 0 && !!st.set && !purchasable(st.set));

      const reach = Math.min(st.maxStepReached, maxAllowedStep(st));
      if (els.progress) els.progress.style.setProperty('--dv-cz-step', String(st.step)); // glass lens position
      els.progressBtns.forEach((btn) => {
        const idx = Number(btn.getAttribute('data-dv-cz-step'));
        const li = btn.closest('li');
        const current = idx === st.step;
        const done = !current && idx <= reach;
        li.classList.toggle('is-current', current);
        li.classList.toggle('is-done', done);
        if (current) btn.setAttribute('aria-current', 'step'); else btn.removeAttribute('aria-current');
        if (!current && !done) btn.setAttribute('aria-disabled', 'true'); else btn.removeAttribute('aria-disabled');
      });

      // Set step
      els.setCards.forEach((card) => {
        const key = card.getAttribute('data-dv-cz-set');
        const sel = key === st.set;
        card.classList.toggle('is-selected', sel);
        const btn = card.querySelector('[data-dv-cz-set-choice]');
        if (btn) {
          setPressed(btn, sel);
          btn.disabled = !selectable(key); // Liquid disables unavailable sets; ?cz_preview=1 re-enables them
          const label = btn.querySelector('[data-dv-cz-choose-label]');
          let text = null;
          if (purchasable(key)) text = sel ? t('dv_cz_selected', 'Selected') : t('dv_cz_choose', 'Choose');
          else if (selectable(key)) text = t('dv_cz_set_preview', 'Preview only');
          if (label && text && label.textContent.trim() !== text) label.textContent = text;
        }
      });

      // Chosen strip
      if (st.set) {
        const card = cardFor(st.set);
        if (els.chosenName) els.chosenName.textContent = setName(st.set);
        const price = card ? card.getAttribute('data-price-label') : '';
        if (els.chosenSpec) {
          const meta = SETS[st.set];
          const cm = iso((st.orientation === 'h' ? [meta.cm[1], meta.cm[0]] : meta.cm).join(' × '));
          const priceOn = !!price && purchasable(st.set);
          els.chosenSpec.textContent = t('dv_cz_js_size_short', '{pieces} pieces · {cm} cm', { pieces: fmtNum(meta.pieces), cm }) + (priceOn ? ' · ' : '');
          if (priceOn) {
            // Price in its own element with the base cents: DAILE Friday code → assets/mn-dfriday.js shows −30%.
            const pe = document.createElement('span');
            pe.setAttribute('data-mn-price-cents', String(priceOf(SET_CFG[st.set])));
            pe.textContent = iso(price);
            els.chosenSpec.appendChild(pe);
          }
        }
        const src = card && card.getAttribute('data-thumb');
        if (els.chosenImg && src && els.chosenImg.getAttribute('src') !== src) els.chosenImg.setAttribute('src', src);
      }

      // Upload step
      if (st.set) positionZone(st);
      els.orientBtns.forEach((b) => setPressed(b, b.getAttribute('data-dv-cz-orient') === st.orientation));
      if (els.size && st.set) els.size.textContent = sizeLine(st);
      show(els.dropzone, !st.photo);
      show(els.photoControls, !!st.photo);
      if (els.quality && st.photo) {
        const q = cropQuality(st);
        const texts = {
          green: t('js_q_green', 'Excellent quality — perfect for print.'),
          yellow: t('js_q_yellow', 'Good quality. For the very best result we recommend a higher-resolution photo.'),
          red: t('js_q_red', 'May look blurry at full print size. We recommend a better photo — you can continue at your own risk.'),
          blocked: t('js_err_crop', 'This crop is too small for print. Zoom out or upload a larger photo.'),
        };
        if (els.quality.textContent !== texts[q]) els.quality.textContent = texts[q];
        els.quality.className = 'dv-cz__quality is-' + q;
      }
      if (els.zone) {
        const has = !!st.photo && !!photoCanvas;
        els.zone.classList.toggle('has-photo', has);
        els.zone.classList.toggle('is-empty', !has);
        if (has) els.zone.setAttribute('tabindex', '0'); else els.zone.removeAttribute('tabindex');
        if (has) els.zone.setAttribute('role', 'group'); else els.zone.removeAttribute('role');
      }
      if (els.zoom && !gesturing) els.zoom.value = String(Math.round((st.crop.zoom || 1) * 100));
      if (els.gestureHint && !gestureHintDone) els.gestureHint.hidden = !(step === 'upload' && st.photo);
      if (els.editNote && st.photo) els.editNote.hidden = true;

      // Nav buttons
      const last = st.step === STEPS.length - 1;
      const uploadGate = step === 'upload' && (!st.photo || cropQuality(st) === 'blocked');
      const nfcIssue = st.nfc.enabled ? nfcProblem(st.nfc.url) : '';
      const cardGate = st.card.added && !cardFit.ok; // the card tab's Continue leads to the Review
      const giftGate = step === 'gift' && (!!nfcIssue || !lidFit.ok || (giftTab === 'card' && cardGate));
      const buyGate = last && (atcBusy || !purchasable(st.set));
      els.nextBtns.forEach((btn) => {
        const label = btn.querySelector('[data-dv-cz-next-label]');
        const text = last ? (atcBusy ? busyLabel() : atcLabel()) : t('js_continue', 'Continue');
        if (label && label.textContent !== text) label.textContent = text;
        btn.disabled = !!(uploadGate || giftGate || buyGate);
      });
      /* Why the CTA is locked, next to it in the action bar (visual pass): the
         field that blocks may be off screen, or on the other gift tab. Existing
         strings only; no text for "no photo yet" (the preview says it). */
      let gate = '';
      if (uploadGate && st.photo) gate = t('js_err_crop', 'This crop is too small for print. Zoom out or upload a larger photo.');
      else if (step === 'gift' && nfcIssue) gate = nfcMessage(nfcIssue);
      else if (step === 'gift' && !lidFit.ok) gate = t('dv_cz_js_text_long', 'This text is too long for the lid — please shorten it.');
      else if (step === 'gift' && giftTab === 'card' && cardGate) gate = t('dv_cz_js_card_long', 'This message is too long for the card — please shorten it.');
      else if (last && !atcBusy && st.set && !purchasable(st.set)) gate = t('dv_cz_preview_only', 'Preview only — this set cannot be ordered yet.');
      els.gateHints.forEach((el) => { if (el.textContent !== gate) el.textContent = gate; });

      renderGift(st);
      renderCard(st);
      renderReview(st);
      scheduleVisible(renderDelay);
      renderDelay = 0;
      if (step === 'upload' && gesturing) layoutProxy();
    }

    function renderGift(st) {
      const mini = isMini(st.set);
      els.giftSubs.forEach((el) => { el.hidden = el.getAttribute('data-dv-cz-gift-sub') !== giftTab; });
      els.giftTabs.forEach((b) => {
        const on = b.getAttribute('data-dv-cz-gift-tab') === giftTab;
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      const tablist = els.giftTabs[0] && els.giftTabs[0].parentElement;
      if (tablist) tablist.style.setProperty('--dv-cz-tab', giftTab === 'card' ? '1' : '0'); // wine indicator position
      const boxTouched = st.box.variant !== 'default' || !!String(st.box.text || '').trim() || st.nfc.enabled;
      show(els.badgeBox, boxTouched);
      show(els.badgeCard, st.card.added);

      els.boxTypeGroups.forEach((g) => { g.hidden = g.getAttribute('data-dv-cz-box-type') !== (mini ? 'mini' : 'classic'); });
      const variant = mini && st.box.variant === 'no-daile' ? 'default' : st.box.variant;
      $$('[data-dv-cz-variant]').forEach((b) => setPressed(b, b.getAttribute('data-dv-cz-variant') === variant));
      show(els.textControls, st.box.variant !== 'photo-only');
      if (els.lidText && els.lidText.value !== st.box.text) els.lidText.value = st.box.text;
      exampleDir(els.lidText, st.box.text);
      fieldFont(els.lidText, st.box.font);
      if (els.lidCount) els.lidCount.textContent = t('dv_cz_js_chars_left', '{n} characters left', { n: Math.max(0, LID_MAX_CHARS - (st.box.text || '').length) });
      show(els.lidNote, lidFit.small);
      if (els.lidError) {
        els.lidError.textContent = lidFit.ok ? '' : t('dv_cz_js_text_long', 'This text is too long for the lid — please shorten it.');
        els.lidError.hidden = lidFit.ok;
      }
      describe(els.lidText, [els.lidCount, lidFit.small ? els.lidNote : null, lidFit.ok ? null : els.lidError], !lidFit.ok);
      $$('[data-dv-cz-font]').forEach((b) => setPressed(b, b.getAttribute('data-dv-cz-font') === st.box.font));
      $$('[data-dv-cz-textsize]').forEach((b) => setPressed(b, b.getAttribute('data-dv-cz-textsize') === st.box.sizePreset));
      $$('[data-dv-cz-color]').forEach((b) => setPressed(b, b.getAttribute('data-dv-cz-color').toUpperCase() === String(st.box.color).toUpperCase()));

      $$('[data-dv-cz-nfc-toggle]').forEach((b) => setPressed(b, (b.getAttribute('data-dv-cz-nfc-toggle') === 'on') === st.nfc.enabled));
      show(els.nfcRow, st.nfc.enabled);
      if (els.nfcUrl && els.nfcUrl.value !== st.nfc.url) els.nfcUrl.value = st.nfc.url;
      const nfcIssue = st.nfc.enabled ? nfcProblem(st.nfc.url) : '';
      if (els.nfcError) {
        const msg = nfcIssue ? nfcMessage(nfcIssue) : '';
        if (msg && els.nfcError.textContent !== msg) els.nfcError.textContent = msg;
        els.nfcError.hidden = !nfcIssue;
      }
      describe(els.nfcUrl, [nfcIssue ? els.nfcError : null], !!nfcIssue);
    }

    /* Example placeholders (owner 2026-09-29) on an RTL page: dir="auto" on an
       empty field resolves to ltr, which set the Arabic example as an LTR line
       (punctuation on the wrong side). Empty = the page direction; once the
       customer types, dir="auto" follows the text again, as before. */
    function exampleDir(field, value) {
      if (!field) return;
      if (exampleDir.rtl === undefined) exampleDir.rtl = window.getComputedStyle(root).direction === 'rtl';
      if (!exampleDir.rtl) return;
      const d = String(value || '') ? 'auto' : 'rtl';
      if (field.getAttribute('dir') !== d) field.setAttribute('dir', d);
    }

    /* Owner 2026-09-30 (round 5): the lid text and the card message are typed in
       the font chosen for them (CSS maps data-font to the family and size). */
    function fieldFont(field, font) {
      if (!field) return;
      const f = FONTS[font] ? font : 'cormorant';
      if (field.getAttribute('data-font') !== f) field.setAttribute('data-font', f);
    }

    /** aria-describedby = the visible helper texts of a field; aria-invalid with an error. */
    function describe(field, helpers, invalid) {
      if (!field) return;
      const ids = helpers.filter((el) => el && el.id).map((el) => el.id).join(' ');
      if (ids) { if (field.getAttribute('aria-describedby') !== ids) field.setAttribute('aria-describedby', ids); } else field.removeAttribute('aria-describedby');
      if (invalid) field.setAttribute('aria-invalid', 'true'); else field.removeAttribute('aria-invalid');
    }

    function renderCard(st) {
      const added = st.card.added;
      $$('[data-dv-cz-card-toggle]').forEach((b) => setPressed(b, (b.getAttribute('data-dv-cz-card-toggle') === 'on') === added));
      if (els.cardInput) {
        els.cardInput.readOnly = !added;
        els.cardInput.classList.toggle('is-locked', !added);
        if (!added) els.cardInput.setAttribute('aria-disabled', 'true'); else els.cardInput.removeAttribute('aria-disabled');
        if (els.cardInput.value !== st.card.text) els.cardInput.value = st.card.text;
        exampleDir(els.cardInput, st.card.text);
        fieldFont(els.cardInput, added ? st.card.font : 'cormorant');
      }
      show(els.cardHint, !added);
      if (els.cardCount) {
        els.cardCount.hidden = !added;
        els.cardCount.textContent = t('dv_cz_js_chars_left', '{n} characters left', { n: Math.max(0, CARD_MAX_CHARS - (st.card.text || '').length) });
      }
      if (els.cardError) {
        els.cardError.hidden = cardFit.ok;
        els.cardError.textContent = cardFit.ok ? '' : t('dv_cz_js_card_long', 'This message is too long for the card — please shorten it.');
      }
      // Review fix: the description pointed at the hidden "select Add card first" hint after Add card.
      describe(els.cardInput, added ? [els.cardCount, cardFit.ok ? null : els.cardError] : [els.cardHint], added && !cardFit.ok);
      const groups = [['data-dv-cz-card-font', st.card.font], ['data-dv-cz-card-textsize', st.card.sizePreset], ['data-dv-cz-card-color', st.card.color], ['data-dv-cz-card-align', st.card.align || 'justify'], ['data-dv-cz-card-orient', cardOrient(st)]];
      groups.forEach(([attr, val]) => {
        $$('[' + attr + ']').forEach((b) => {
          setPressed(b, String(b.getAttribute(attr)).toUpperCase() === String(val).toUpperCase());
          b.disabled = !added;
        });
      });
    }

    function renderReview(st) {
      if (!st.set || !els.rwTotal) return;
      const mini = isMini(st.set);
      const setCfg = SET_CFG[st.set];
      if (els.rwSet) els.rwSet.textContent = setName(st.set);
      if (els.rwOrient) els.rwOrient.textContent = t('dv_cz_js_pieces', '{n} pieces', { n: fmtNum(SETS[st.set].pieces) }) + ' · ' + orientLine(st);
      if (els.rwBase) els.rwBase.innerHTML = purchasable(st.set) ? priceHtml(setCfg) : '';

      const noLogo = !mini && st.box.variant === 'no-daile';
      if (els.rwBox) {
        const names = {
          default: mini ? t('dv_cz_js_lid_mini', 'Basic — photo & your text') : t('js_lid_default', 'Basic — photo, DAILE logo & your text'),
          'photo-only': t('js_lid_photo_only', 'Photo — just your photo, centred'),
          'no-daile': t('js_lid_nologo', 'Personal — your photo & large text, no logo'),
        };
        let line = names[mini && st.box.variant === 'no-daile' ? 'default' : st.box.variant] || '';
        const msg = lidMessage(st).replace(/\s+/g, ' ');
        if (msg) line += ' · ' + iso('“' + (msg.length > 26 ? msg.slice(0, 26) + '…' : msg) + '”');
        els.rwBox.textContent = line;
      }
      show(els.rwBoxPrice, noLogo);
      if (els.rwBoxPrice && noLogo) els.rwBoxPrice.innerHTML = priceHtml(ADDONS.nologo, true);
      show(els.rwBoxX, noLogo);

      const hasNfc = st.nfc.enabled && !!nfcNormalize(st.nfc.url) && validNfcUrl(st.nfc.url);
      show(els.rwNfcRow, hasNfc);
      if (els.rwNfc && hasNfc) els.rwNfc.textContent = iso(nfcNormalize(st.nfc.url));

      show(els.rwCardRow, st.card.added);
      if (st.card.added) {
        const msg = String(st.card.text || '').replace(/\s+/g, ' ').trim();
        if (els.rwCard) els.rwCard.textContent = msg ? iso('“' + (msg.length > 34 ? msg.slice(0, 34) + '…' : msg) + '”') : t('js_no_message', 'No message yet');
        if (els.rwCardPrice) els.rwCardPrice.innerHTML = priceHtml(ADDONS.card, true);
      }

      // Glue: Mini only, and only while the glue-kit variant is on sale (it tracks stock).
      const glueOffered = mini && addonOk('glue');
      const glueOn = glueOffered && st.addons.glue;
      show(els.rwGlueRow, glueOffered);
      if (els.rwGluePrice) els.rwGluePrice.innerHTML = glueOffered ? priceHtml(ADDONS.glue, true) : '';
      show(els.rwGlueAdd, glueOffered && !glueOn);
      show(els.rwGlueX, glueOn);
      if (els.rwGlueRow) els.rwGlueRow.classList.toggle('is-on', glueOn);

      show(els.rwBagRow, st.extras.bag);
      if (els.rwBagPrice && st.extras.bag) els.rwBagPrice.innerHTML = priceHtml(ADDONS.bag, true);
      show(els.rwMagnetsRow, st.extras.magnets);
      if (els.rwMagnetsPrice && st.extras.magnets) els.rwMagnetsPrice.innerHTML = priceHtml(ADDONS.magnets, true);
      renderKept(st);

      const parts = [setCfg];
      if (noLogo) parts.push(ADDONS.nologo);
      if (st.card.added) parts.push(ADDONS.card);
      if (glueOn) parts.push(ADDONS.glue);
      if (st.extras.bag) parts.push(ADDONS.bag);
      if (st.extras.magnets) parts.push(ADDONS.magnets);
      (st.kept || []).forEach((k) => parts.push({ price: k.price * (k.quantity || 1) }));
      const total = parts.reduce((sum, p) => sum + priceOf(p), 0);
      const full = parts.reduce((sum, p) => sum + fullOf(p), 0);
      els.rwTotal.innerHTML = purchasable(st.set) ? '<span dir="ltr">' + (full > total ? '<s data-mn-df-hide>' + fmtMoney(full) + '</s> ' : '') + '<span data-mn-price-cents="' + total + '">' + fmtMoney(total) + '</span></span>' : '';
      if (els.navTotal) {
        els.navTotal.setAttribute('data-mn-price-cents', String(total)); // DAILE Friday base (assets/mn-dfriday.js)
        els.navTotal.textContent = purchasable(st.set) ? fmtMoney(total) : ''; // sticky bar (≤900)
      }
    }

    /* Rows for the cart-drawer add-ons carried over by an edit (st.kept): a
       copy of the bag row with the cart's title and price, and a × that drops
       the add-on from the updated line. Rebuilt only when the list changes. */
    let keptSig = '';
    const keptUid = randHex(3);
    function renderKept(st) {
      const kept = st.kept || [];
      const sig = JSON.stringify(kept.map((k) => [k.id, k.title, k.price, k.quantity]));
      if (sig === keptSig || !els.rwList || !els.rwBagRow) return;
      keptSig = sig;
      $$('[data-dv-cz-rw-kept]').forEach((row) => row.remove());
      const after = els.rwMagnetsRow || els.rwBagRow;
      let anchor = after;
      kept.forEach((k, i) => {
        const row = els.rwBagRow.cloneNode(true);
        row.removeAttribute('data-dv-cz-rw-bag-row');
        row.setAttribute('data-dv-cz-rw-kept', String(i));
        row.hidden = false;
        const label = row.querySelector('b');
        const id = 'dv-cz-rw-kept-' + i + '-' + keptUid;
        if (label) { label.textContent = k.title + (k.quantity > 1 ? ' × ' + k.quantity : ''); label.id = id; }
        const price = row.querySelector('[data-dv-cz-rw-bag-price]');
        if (price) { price.removeAttribute('data-dv-cz-rw-bag-price'); price.innerHTML = priceHtml({ price: k.price * (k.quantity || 1) }, true); }
        const x = row.querySelector('[data-dv-cz-rw-remove]');
        if (x) { x.setAttribute('data-dv-cz-rw-remove', 'kept:' + i); x.setAttribute('aria-describedby', id); }
        anchor.after(row);
        anchor = row;
      });
    }

    /* ------------------------------------------------------ add to cart */

    const atcLabel = () => (editingLineId ? t('js_update_cart', 'Update in cart') : t('js_add_cart', 'Add to cart'));

    function showAtcError(msg) {
      if (!els.atcError) return;
      els.atcError.textContent = msg || '';
      els.atcError.hidden = !msg;
    }

    function setAtcBusyUi(busy, keepLocked) {
      root.inert = busy;
      root.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (busy && !lockedControls) {
        lockedControls = $$('button, input, textarea, select').map((control) => {
          const entry = { control, disabled: control.disabled };
          control.disabled = true;
          return entry;
        });
      }
      if (!busy && lockedControls && !keepLocked) {
        lockedControls.forEach((entry) => { entry.control.disabled = entry.disabled; });
        lockedControls = null;
      }
    }

    /** Small puzzle-crop thumbnail for the drawer (old js:1161-1186, set aspect). */
    function makeCartThumb(st) {
      try {
        if (!photoCanvas || !st.set) return null;
        const [tw0, th0] = trimFor(st.set, st.orientation);
        const aspect = tw0 / th0;
        const tw = aspect < 1 ? 180 : Math.round(180 * aspect);
        const th = aspect < 1 ? Math.round(180 / aspect) : 180;
        const r = cropRect(photoCanvas.width, photoCanvas.height, aspect, st.crop, bleedFor(st.set, st.orientation));
        const cv = document.createElement('canvas');
        cv.width = tw;
        cv.height = th;
        const ctx = cv.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(photoCanvas, r[0] * photoCanvas.width, r[1] * photoCanvas.height, r[2] * photoCanvas.width, r[3] * photoCanvas.height, 0, 0, tw, th);
        return cv.toDataURL('image/jpeg', 0.72);
      } catch (e) {
        return null;
      }
    }

    function removeOldLine(oldId) {
      return fetchT(SHOP_ROOT + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }, CART_TIMEOUT_MS)
        .then((r) => { if (!r.ok) throw new Error('cart verification failed'); return r.json(); })
        .then((cart) => {
          const updates = {};
          (cart.items || []).forEach((item) => {
            const p = item.properties || {};
            if (p._line_id === oldId || p._parent_line === oldId) updates[item.key] = 0;
          });
          if (!Object.keys(updates).length) return null;
          return fetchT(SHOP_ROOT + 'cart/update.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }),
          }, CART_TIMEOUT_MS).then((response) => { if (!response.ok) throw new Error('cart verification cleanup failed'); });
        });
    }

    /** config v2 = snapshot of what the customer saw (CONTRACT §10). */
    function buildDesign(st, previewOk) {
      const meta = SETS[st.set];
      const cfg = SET_CFG[st.set] || {};
      const trim = trimFor(st.set, st.orientation);
      const cm = st.orientation === 'h' ? [meta.cm[1], meta.cm[0]] : meta.cm.slice();
      const slot = puzzleScene(st).slots[0];
      const c = photoCanvas
        ? clampCrop(photoCanvas.width, photoCanvas.height, slot.aspect, st.crop, bleedFor(st.set, st.orientation))
        : st.crop;
      const crop = { zoom: round(c.zoom, 4), nx: round(c.nx, 5), ny: round(c.ny, 5) };
      const layout = lidLayoutId(st);
      const lidRects = lidRectsOf(st, layout);
      const lidSize = LID_SIZE_MM[meta.box];
      const message = lidMessage(st);
      let lidText = null;
      if (message) {
        const resolved = window.DVCZ.resolveLayout(lidScene(st), layout);
        const fit = window.DVCZ.fitText(resolved.text_slots.message, message, lidTextOpts(st));
        const rel = fit.fontPx / fit.texWidth;
        const f = faceFor(st.box.font, message);
        lidText = {
          value: message,
          lines: fit.lines,
          font: st.box.font,
          font_family: f.family,
          font_weight: f.weight,
          ...(f.print ? { print_font: f.print } : {}), // 1.3.6: Work v3.29 prints this face
          color: st.box.color,
          align: 'center',
          size_preset: st.box.sizePreset,
          size_rel: round(rel, 5),                                    // font size / text-field width
          size_mm: lidSize && lidRects && lidRects.text ? round(rel * (lidRects.text[2] - lidRects.text[0]) * lidSize[0], 2) : null,
          line_height: 1.12,
          padding: 0.055,
        };
      }
      let card = null;
      if (st.card.added) {
        const text = String(st.card.text || '');
        let lines = null;
        if (text.trim() && cardSlotOf(st)) {
          try { lines = window.DVCZ.fitText(cardSlotOf(st), text, cardTextOpts(st)).lines; } catch (e) { lines = null; }
        }
        const co = cardOrient(st);
        card = {
          text, font: st.card.font, sizePreset: st.card.sizePreset, color: st.card.color, align: st.card.align || 'justify',
          mode: text.trim() ? 'photo-text' : 'photo', size_mm: co === 'h' ? CARD_SIZE_MM.slice().reverse() : CARD_SIZE_MM.slice(), lines,
          orientation: co, // 1.3.3: Work prints both sides in this orientation (CUSTOMIZER-CONTRACT §10.8)
        };
        const cf = faceFor(st.card.font, text);
        if (cf.print) card.print_font = cf.print; // 1.3.6: Work v3.29 prints this face
      }
      const mini = isMini(st.set);
      return {
        schema_version: 2,
        v: 2,
        app: 'dv-customizer/' + VERSION,
        created_at: new Date().toISOString(),
        locale: LOCALE,
        set: st.set,
        set_title: cfg.title || meta.title,
        variant_id: Number(cfg.variant_id) || 0,
        variant_fallback: !!cfg.fallback,
        pieces: meta.pieces,
        size: String(meta.pieces),
        box_type: meta.box,
        orientation: st.orientation,
        size_cm: cm,
        trim_mm: trim,
        bleed_mm: BLEED_MM,
        bleed: { short: round((2 * BLEED_MM) / meta.trim[0], 6), long: round((2 * BLEED_MM) / meta.trim[1], 6) },
        crop,
        crops: { puzzle: crop },
        photo: st.photo ? {
          w: st.photo.width, h: st.photo.height, quality: cropQuality(st),
          line: sessionLineId, token: photoToken,
          originalUrl: WORKER ? photoUrl('original') : null,
          previewUrl: WORKER && previewOk ? photoUrl('preview') : null,
          // Only when the original could not be sent and the prepared copy went instead (uploadOriginal).
          original_fallback: uploadedFallback ? { source: 'prepared-jpeg', reason: uploadedFallback.reason, w: uploadedFallback.w, h: uploadedFallback.h } : undefined,
          // 1.3.1: DAILE STUDIO artwork (null/absent = the customer's own photo)
          origin: photoOrigin || undefined,
        } : null,
        scenes: {
          manifest_rev: (manifest.source && manifest.source.layout_revision) || null,
          puzzle: puzzleScene(st).id,
          with_box: withBoxScene(st).id,
          lid: lidScene(st).id,
          postcard: st.card.added ? cardSceneId(st) : null,
        },
        lid: {
          layout,
          logo: !mini && st.box.variant === 'default',
          size_mm: lidSize ? lidSize.slice() : null,
          photo_rect: lidRects ? lidRects.photo.slice() : null,
          text_rect: lidRects && lidRects.text ? lidRects.text.slice() : null,
          text: lidText,
        },
        box: { ...st.box, variant: mini && st.box.variant === 'no-daile' ? 'default' : st.box.variant },
        card,
        nfc: st.nfc.enabled && nfcNormalize(st.nfc.url) ? nfcNormalize(st.nfc.url) : null,
        addons: { glue: mini && !!st.addons.glue, nologo: !mini && st.box.variant === 'no-daile', card: !!st.card.added },
        extras: { bag: !!st.extras.bag, magnets: !!st.extras.magnets, frameInterest: !!st.extras.frameInterest },
        // derived.lidTextUnits of v1 described the OLD flat lid mock-up; it is not
        // written for the new layouts (production v1 falls back to its auto-fit).
        derived: {},
      };
    }

    /** Final upload failure at Add to cart (after all retries): one analytics event + console line,
       no personal data (no line id, token or photo), so the next case can be read in GA / pixels. */
    function reportUploadFailure(e) {
      const file = currentFile;
      const info = {
        stage: (e && e.stage) || 'original',
        status: (e && e.status) || 0,
        message: describeError(e),
        attempts: (e && e.attempts) || 0,
        fileType: file ? file.type : (photoCanvas ? 'canvas' : ''),
        fileSizeKB: file ? Math.round(file.size / 1024) : 0,
        ua: String(navigator.userAgent || '').slice(0, 250),
        online: navigator.onLine !== false,
      };
      console.error('[dv-customizer] cz_upload_failed', info);
      track('cz_upload_failed', info);
    }

    function addToCart() {
      if (atcBusy) return;
      const st = store.getState();
      if (maxAllowedStep(st) < STEPS.length - 1) {
        if (maxAllowedStep(st) === 2) giftTab = giftGateTab(st); // open the tab that holds the problem
        goTo(maxAllowedStep(st), { keepTab: true });
        return;
      }
      if (!purchasable(st.set)) { showAtcError(t('dv_cz_js_set_unavailable', 'This set cannot be ordered yet. Please choose another set.')); return; }
      const mini = isMini(st.set);
      const setCfg = SET_CFG[st.set];
      const useNologo = !mini && st.box.variant === 'no-daile';
      const useGlue = mini && st.addons.glue && addonOk('glue'); // the Review hides a glue kit that is out of stock
      const vid = (a) => (ADDONS[a] && Number(ADDONS[a].variant_id)) || 0;
      const missing = [];
      if (!Number(setCfg.variant_id)) missing.push('puzzle');
      if (useNologo && !vid('nologo')) missing.push('no-logo lid');
      if (st.card.added && !vid('card')) missing.push('card');
      if (useGlue && !vid('glue')) missing.push('glue');
      if (st.extras.bag && !vid('bag')) missing.push('bag');
      if (st.extras.magnets && !vid('magnets')) missing.push('magnets');
      if (missing.length) {
        console.error('[dv-customizer] missing products', missing);
        showAtcError(t('js_err_setup', 'This option is temporarily unavailable. Please try again later.'));
        return;
      }
      showAtcError(null);
      atcBusy = true;
      setAtcBusyUi(true);
      render(store.getState());
      announce(t('dv_cz_live_saving', 'Saving your photo and design…'));

      const meta = SETS[st.set];
      const thumb = makeCartThumb(st);
      let clicked = false;
      let rotations = 0;

      /* Original (a background upload in flight is joined; if that one fails after its own
         retries, one fresh run follows), preview, then config + cart/add. The line id is read
         after the uploads: a 409 from the Worker moves the order to a new line (rotateSession). */
      const ensureCloud = () => {
        const src = photoSource();
        const joined = !!(uploadPromise && uploadPromise.dvFile === src);
        return uploadOriginal()
          .catch((e) => {
            if (!joined || (e && (e.aborted || e.conflict))) throw e;
            console.warn('[dv-customizer] background upload failed, starting again', e);
            return uploadOriginal();
          })
          .then((ok) => {
            if (!ok) throw new Error('cloud original upload failed');
            return uploadPreview(thumb);
          });
      };

      const placeOrder = () => ensureCloud().then((previewOk) => {
        const lineId = sessionLineId;
        const cm = (st.orientation === 'h' ? [meta.cm[1], meta.cm[0]] : meta.cm).join(' × ');
        // Lid names the customer sees in the cart / checkout / order e-mails (owner 2026-10-06:
        // Basic / Photo / Personal; until then Default / Photo only / No logo, Mini "Photo & text").
        // Production reads the layout from _design (box.variant), never this label.
        const lidNames = mini
          ? { default: 'Basic', 'photo-only': 'Photo', 'no-daile': 'Basic' }
          : { default: 'Basic', 'photo-only': 'Photo', 'no-daile': 'Personal' };
        // Visible keys stay English, as in v1: staff and production read them.
        const props = {
          Size: meta.pieces + ' pieces',
          Orientation: (st.orientation === 'h' ? 'Landscape ' : 'Portrait ') + cm + ' cm',
          Lid: lidNames[st.box.variant] || 'Basic',
        };
        const message = lidMessage(st);
        if (message) props['Lid text'] = message;
        if (setCfg.fallback) props['Gift set'] = setCfg.title || meta.title; // never "Set" (CONTRACT §3.5)
        props._line_id = lineId;
        if (st.nfc.enabled && nfcNormalize(st.nfc.url)) props._nfc_url = nfcNormalize(st.nfc.url);
        const sheets = String(CONFIG.sheets || SHEETS_DEFAULT).replace(/\/$/, '');
        if (sheets) props._order_sheet = sheets + '/s/' + lineId + '?t=' + photoToken;
        if (st.extras.bag) props._cz_bag = 'yes';
        if (st.extras.frameInterest) props._frame_interest = 'yes';
        const designStr = JSON.stringify(buildDesign(st, previewOk));
        props._design = designStr;

        const items = [{ id: Number(setCfg.variant_id), quantity: 1, properties: props }];
        if (useNologo) items.push({ id: vid('nologo'), quantity: 1, properties: { _parent_line: lineId, _addon: 'lid' } });
        if (st.card.added) items.push({ id: vid('card'), quantity: 1, properties: { _parent_line: lineId, 'Card message': st.card.text || '—' } });
        if (useGlue) items.push({ id: vid('glue'), quantity: 1, properties: { _parent_line: lineId, _set: 'Glue Kit' } }); // = art Mini (daile-cart.js:620-622)
        if (st.extras.bag) items.push({ id: vid('bag'), quantity: 1, properties: { _parent_line: lineId } });
        if (st.extras.magnets) items.push({ id: vid('magnets'), quantity: 1, properties: { _parent_line: lineId } });
        // Edit: add-ons the drawer had attached to the old line, re-created as they were under the new one.
        (st.kept || []).forEach((k) => items.push({ id: Number(k.id), quantity: k.quantity || 1, properties: { ...(k.properties || {}), _parent_line: lineId } }));

        if (!clicked) { clicked = true; track('add_to_cart_clicked', { items: items.length, set: st.set }); }
        return saveDesignAndAddItems({
          shopRoot: SHOP_ROOT,
          lineId,
          configUrl: uploadUrl('config'),
          design: designStr,
          items,
        }, submission).then(() => lineId);
      }).catch((e) => {
        // 409 "owned by another session" on the config: new line + token, original again, then config.
        if (e && e.conflict && rotations < 2 && !submission.attempted) {
          rotations += 1;
          rotateSession('409 on ' + (e.stage || 'upload'));
          return placeOrder();
        }
        throw e;
      });

      placeOrder()
        .then((lineId) => {
          try { if (thumb) window.sessionStorage.setItem('daile_cz_preview_' + lineId, thumb); } catch (e) { /* quota / private mode */ }
          const saves = [];
          if (currentFile) saves.push(idbSavePhoto(lineId, currentFile));
          if (editingLineId) saves.push(removeOldLine(editingLineId));
          return Promise.all(saves);
        })
        .then(() => {
          submission.finished = true;
          const glue = PDP_URL.indexOf('?') > -1 ? '&' : '?';
          window.location.href = PDP_URL + glue + 'daile_cart=open';
        })
        .catch((e) => {
          console.error('[dv-customizer] add to cart', e);
          atcBusy = false;
          uploadProgress = null;
          /* Shopify answered cart/add with 4xx (e.g. 422 sold out): the customer
             may change the design, so the whole UI is unlocked (old bug B2 kept it
             locked). A retry still goes through reconcileSubmission, so a line that
             did get added is never duplicated. After an ambiguous failure (network)
             the design stays locked and only the idempotent submit is offered. */
          const addRejected = !!(e && !e.stage && e.status >= 400 && e.status < 500); // upload errors carry .stage
          if (!submission.attempted && /upload/.test((e && e.message) || '')) reportUploadFailure(e);
          setAtcBusyUi(false, submission.attempted && !addRejected);
          /* A refusal keeps Shopify's own description when there is one: it names
             the product (e.g. a sold-out glue kit) in the shop's language. */
          const msg = addRejected
            ? (e.description || t('js_err_setup', 'This option is temporarily unavailable. Please try again later.'))
            : submission.attempted || /cart verification/.test((e && e.message) || '')
              ? t('js_err_cart_check', 'We could not confirm your cart. Check your connection and try again — your design is still here.')
              : /upload/.test((e && e.message) || '')
                ? t('js_err_save', 'We could not save your photo — please check your connection and try again.')
                : t('js_err_generic', 'Something went wrong — please try again.');
          showAtcError(msg);
          render(store.getState()); // re-enables the submit button (buyGate is false again)
          /* The inert section dropped focus to <body> (review fix): back to the
             visible CTA, next to the message. */
          const cta = els.nextBtns.find((b) => b.offsetParent && !b.disabled);
          focusQuiet(cta);
        });
    }

    /* ------------------------------------------------------------ events */

    on(root, 'click', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      const choice = target.closest('[data-dv-cz-set-choice]');
      if (choice) { if (!choice.disabled) chooseSet(choice.getAttribute('data-dv-cz-set-choice')); return; }

      if (target.closest('[data-dv-cz-change-set]')) {
        const st = store.getState();
        returnStep = st.step;
        store.set({ step: 0 });
        scrollToTop();
        focusStep();
        return;
      }

      const next = target.closest('[data-dv-cz-next]');
      if (next) {
        if (next.disabled) return;
        const st = store.getState();
        if (st.step === STEPS.length - 1) { addToCart(); return; }
        if (STEPS[st.step] === 'gift' && giftTab === 'box') { setGiftTab('card'); scrollToTop(); focusStep(); return; }
        track('step_completed', { step: STEPS[st.step], step_index: st.step, set: st.set || '' });
        goTo(st.step + 1);
        return;
      }

      const back = target.closest('[data-dv-cz-back]');
      if (back) {
        const st = store.getState();
        if (STEPS[st.step] === 'gift' && giftTab === 'card') { setGiftTab('box'); scrollToTop(); focusStep(); return; } // like Continue (the Back pressed hides with the card panel)
        if (STEPS[st.step - 1] === 'gift') giftTab = 'card';
        if (st.step === 1) { returnStep = null; store.set({ step: 0 }); scrollToTop(); focusStep(); return; }
        goTo(st.step - 1);
        return;
      }

      const stepBtn = target.closest('[data-dv-cz-step]');
      if (stepBtn) {
        const idx = Number(stepBtn.getAttribute('data-dv-cz-step'));
        const st = store.getState();
        if (idx === 0 && st.step !== 0) { returnStep = st.step; store.set({ step: 0 }); scrollToTop(); focusStep(); return; }
        if (idx !== st.step && idx <= Math.min(st.maxStepReached, maxAllowedStep(st))) goTo(idx);
        return;
      }

      const tab = target.closest('[data-dv-cz-gift-tab]');
      if (tab) { setGiftTab(tab.getAttribute('data-dv-cz-gift-tab')); return; }

      const orient = target.closest('[data-dv-cz-orient]');
      if (orient) {
        const v = orient.getAttribute('data-dv-cz-orient');
        if (v !== store.getState().orientation) store.set({ orientation: v, crop: { zoom: 1, nx: 0, ny: 0 } }); // other zone => crop from scratch (old js:1557-1565)
        return;
      }

      const pick = target.closest('[data-dv-cz-pick]');
      if (pick) { if (photoProcessing) { e.preventDefault(); return; } pickStarted(); return; } // the <label> opens the chooser itself
      if (target.closest('[data-dv-cz-browse]')) { browse(); return; }
      const zoneClick = target.closest('[data-dv-cz-zone]');
      if (zoneClick && !store.getState().photo) { browse(); return; }

      if (target.closest('[data-dv-cz-remove]')) {
        if (designStarted()) { confirmPending = 'remove'; openDialog(els.confirmDialog); } else { removePhoto(); focusBrowse(); }
        return;
      }

      const variant = target.closest('[data-dv-cz-variant]');
      if (variant) {
        const v = variant.getAttribute('data-dv-cz-variant');
        if (v !== store.getState().box.variant) {
          store.merge('box', { variant: v });
          track('composition_type_selected', { type: v, set: store.getState().set || '' });
        }
        return;
      }
      const font = target.closest('[data-dv-cz-font]');
      if (font) { store.merge('box', { font: font.getAttribute('data-dv-cz-font') }); return; }
      const tsize = target.closest('[data-dv-cz-textsize]');
      if (tsize) { store.merge('box', { sizePreset: tsize.getAttribute('data-dv-cz-textsize') }); return; }
      const color = target.closest('[data-dv-cz-color]');
      if (color) { store.merge('box', { color: color.getAttribute('data-dv-cz-color') }); return; }

      const nfcT = target.closest('[data-dv-cz-nfc-toggle]');
      if (nfcT) {
        const onNfc = nfcT.getAttribute('data-dv-cz-nfc-toggle') === 'on';
        if (onNfc !== store.getState().nfc.enabled) {
          store.merge('nfc', { enabled: onNfc });
          track('nfc_selected', { enabled: onNfc });
          if (onNfc && els.nfcUrl) window.setTimeout(() => els.nfcUrl.focus(), 0);
        }
        return;
      }
      const dlg = target.closest('[data-dv-cz-open-dialog]');
      if (dlg) { openDialog(dlg.getAttribute('data-dv-cz-open-dialog') === 'nfc' ? els.nfcDialog : null, dlg); return; }

      const cardT = target.closest('[data-dv-cz-card-toggle]');
      if (cardT) {
        const add = cardT.getAttribute('data-dv-cz-card-toggle') === 'on';
        if (add !== store.getState().card.added) {
          store.merge('card', { added: add });
          track(add ? 'card_added' : 'card_removed', {});
          if (add && els.cardInput) window.setTimeout(() => els.cardInput.focus(), 0);
        }
        return;
      }
      const cf = target.closest('[data-dv-cz-card-font]');
      if (cf) { store.merge('card', { font: cf.getAttribute('data-dv-cz-card-font') }); return; }
      const cs = target.closest('[data-dv-cz-card-textsize]');
      if (cs) { store.merge('card', { sizePreset: cs.getAttribute('data-dv-cz-card-textsize') }); return; }
      const cc = target.closest('[data-dv-cz-card-color]');
      if (cc) { store.merge('card', { color: cc.getAttribute('data-dv-cz-card-color') }); return; }
      const ca = target.closest('[data-dv-cz-card-align]');
      if (ca) { store.merge('card', { align: ca.getAttribute('data-dv-cz-card-align') }); return; }
      const co = target.closest('[data-dv-cz-card-orient]');
      if (co) { store.merge('card', { orient: co.getAttribute('data-dv-cz-card-orient') }); return; }

      if (target.closest('[data-dv-cz-glue-add]')) {
        store.merge('addons', { glue: true });
        track('glue_added', { set: store.getState().set || '' });
        focusQuiet(els.rwGlueX); // "Add" hides itself; its row now offers ×
        return;
      }
      const rm = target.closest('[data-dv-cz-rw-remove]');
      if (rm) {
        const what = rm.getAttribute('data-dv-cz-rw-remove');
        if (what === 'card') { store.merge('card', { added: false }); track('card_removed', {}); }
        else if (what === 'glue') store.merge('addons', { glue: false });
        else if (what === 'bag') store.merge('extras', { bag: false });
        else if (what === 'magnets') store.merge('extras', { magnets: false });
        else if (what === 'nodaile') { store.merge('box', { variant: 'default' }); track('composition_type_selected', { type: 'default' }); }
        else if (what === 'nfc') { store.merge('nfc', { enabled: false }); track('nfc_selected', { enabled: false }); }
        else if (/^kept:\d+$/.test(what)) {
          const i = Number(what.slice(5));
          store.set({ kept: (store.getState().kept || []).filter((k, j) => j !== i) });
        }
        /* The × (or its whole row) is hidden now: glue × -> its "Add", no-logo × ->
           the lid row's Edit, any other row -> the Review heading. */
        if (what === 'glue' && els.rwGlueAdd && !els.rwGlueAdd.hidden) focusQuiet(els.rwGlueAdd);
        else if (what === 'nodaile' && els.rwBoxX) focusQuiet(els.rwBoxX.parentElement.querySelector('[data-dv-cz-goto]'));
        else focusStep();
        return;
      }
      const gotoBtn = target.closest('[data-dv-cz-goto]');
      if (gotoBtn) {
        const [name, tab] = String(gotoBtn.getAttribute('data-dv-cz-goto')).split(':');
        const idx = STEPS.indexOf(name);
        const st = store.getState();
        if (idx >= 0 && idx <= Math.min(st.maxStepReached, maxAllowedStep(st))) {
          if (tab) giftTab = tab;
          goTo(idx, { keepTab: !!tab });
        }
      }
    });

    /* Prefetch the puzzle scene of a set on hover / focus. Download only: the
       stage is hidden on the set step, so the render width is unknown here, and
       a layer cache entry at a guessed size was never used (review fix). */
    els.setCards.forEach((card) => {
      const warm = () => {
        const key = card.getAttribute('data-dv-cz-set');
        if (!selectable(key)) return;
        const scene = window.DVCZ.sceneFor({ set: key, orientation: orientName(store.getState().orientation), kind: 'puzzle' });
        if (scene && window.DVCZ.prefetch) window.DVCZ.prefetch(scene, { width: expectedRenderWidth() }).catch(() => {});
      };
      on(card, 'pointerenter', warm, { once: true });
      on(card, 'focusin', warm, { once: true });
    });

    // Gift tabs: arrows move to the other tab, Home / End to the first / last (WAI-ARIA tabs pattern)
    els.giftTabs.forEach((tab) => on(tab, 'keydown', (e) => {
      let next = null;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') next = giftTab === 'box' ? 'card' : 'box';
      else if (e.key === 'Home') next = 'box';
      else if (e.key === 'End') next = 'card';
      if (!next) return;
      e.preventDefault();
      setGiftTab(next, true);
    }));

    // Confirm dialog: yes = reset + action; any other close = cancel
    on($('[data-dv-cz-confirm-yes]'), 'click', () => {
      const pending = confirmPending;
      confirmPending = null;
      if (pending === 'remove' && els.confirmDialog) {
        /* dv.js hands focus back to the opener (Remove) on "close", but Remove
           hides with the photo controls: move focus to Browse after that. This
           listener is on the dialog, so it runs after dv.js's capture listener. */
        els.confirmDialog.addEventListener('close', focusBrowse, { once: true });
      }
      closeDialog(els.confirmDialog);
      if (pending === 'remove') { removePhoto(); store.set(designResetPatch()); }
      else if (pending === 'replace') { pendingReset = true; if (els.file) els.file.click(); }
      else if (pending && pending.file) { pendingReset = true; acceptFile(pending.file, false, true); }
    });
    on(els.confirmDialog, 'close', () => { confirmPending = null; });
    on(els.nfcDialog, 'close', () => {
      const v = els.nfcDialog.querySelector('video');
      if (v) { try { v.pause(); } catch (e) { /* ignore */ } }
    });

    if (els.file) {
      on(els.file, 'change', () => {
        window.clearTimeout(pickT);
        if (els.file.files && els.file.files[0]) pickLabel(t('cz_preparing', 'Preparing your photo…')); else pickReset();
        acceptFile(els.file.files && els.file.files[0], false, true);
        els.file.value = ''; // same file can be picked again after a refusal
      });
      on(els.file, 'cancel', () => { pendingReset = false; pickReset(); }); // old bug B11
    }

    // Drag & drop: on the drop zone and on the preview; elsewhere the drop is swallowed
    const dropTargets = $$('[data-dv-cz-drop]');
    dropTargets.forEach((el) => {
      on(el, 'dragover', (e) => { e.preventDefault(); el.classList.add('is-over'); });
      on(el, 'dragleave', () => el.classList.remove('is-over'));
      on(el, 'drop', (e) => {
        e.preventDefault();
        e.stopPropagation();
        el.classList.remove('is-over');
        const dt = e.dataTransfer;
        dropFile(dt && dt.files && dt.files[0]);
      });
    });
    on(root, 'dragover', (e) => e.preventDefault());
    on(root, 'drop', (e) => {
      e.preventDefault();
      if (STEPS[store.getState().step] === 'upload') {
        const dt = e.dataTransfer;
        dropFile(dt && dt.files && dt.files[0]);
      }
    });

    if (els.zoom) {
      on(els.zoom, 'input', () => {
        if (!photoCanvas) return;
        dismissGestureHint(); // the desktop hint names the slider: using it is the first interaction too
        beginGesture();
        live.zoom = clamp(Number(els.zoom.value) / 100, 1, 3);
        layoutProxy();
      });
      on(els.zoom, 'change', () => { if (gesturing) commitCrop(); });
    }

    if (els.lidText) {
      on(els.lidText, 'input', () => {
        renderDelay = 160;
        store.merge('box', { text: els.lidText.value.slice(0, LID_MAX_CHARS) });
      });
      on(els.lidText, 'change', () => {
        if (!textTracked && els.lidText.value.trim()) { textTracked = true; track('text_added', {}); }
      });
    }
    if (els.cardInput) {
      on(els.cardInput, 'input', () => {
        if (!store.getState().card.added) return;
        renderDelay = 200;
        store.merge('card', { text: els.cardInput.value.slice(0, CARD_MAX_CHARS) });
      });
      // Locked textarea: point at "Add card" instead of trapping focus (old js:1793-1811 blurred it).
      const nudge = () => {
        if (store.getState().card.added) return;
        const btn = $('[data-dv-cz-card-toggle="on"]');
        if (btn && !reducedMotion.matches) {
          btn.classList.remove('is-nudge');
          void btn.offsetWidth;
          btn.classList.add('is-nudge');
          btn.addEventListener('animationend', () => btn.classList.remove('is-nudge'), { once: true });
        }
        if (els.cardHint) {
          els.cardHint.classList.add('is-flash');
          window.setTimeout(() => els.cardHint.classList.remove('is-flash'), 1400);
        }
      };
      on(els.cardInput, 'click', nudge);
      on(els.cardInput, 'focus', nudge);
    }
    if (els.nfcUrl) {
      on(els.nfcUrl, 'input', () => store.merge('nfc', { url: els.nfcUrl.value }));
      // on blur (not on input — the caret would jump): «youtube.com/…» → «https://youtube.com/…»
      on(els.nfcUrl, 'blur', () => {
        const v = nfcNormalize(els.nfcUrl.value);
        if (v !== els.nfcUrl.value) { els.nfcUrl.value = v; store.merge('nfc', { url: v }); }
      });
    }

    let resizeTimer = 0;
    on(window, 'resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => { scheduleVisible(0); if (gesturing) layoutProxy(); }, 150);
    });
    // Back online: a preview that failed to download is drawn again (review fix).
    on(window, 'online', () => { Object.values(views).forEach((v) => { v.failures = 0; }); scheduleVisible(0); });

    /* Back online or back in front (iOS: app switch, screen lock): a background upload of the
       original that gave up meanwhile starts again, so Add to cart finds it done or running. */
    const resumeUpload = () => {
      if (destroyed || atcBusy || !WORKER || document.visibilityState === 'hidden') return;
      const st = store.getState();
      const src = photoSource();
      if (st.step < 2 || !st.photo || !src || uploadedForFile === src || uploadPromise) return;
      uploadOriginal().catch((e) => console.warn('[dv-customizer] background upload', e));
    };
    on(window, 'online', resumeUpload);
    on(document, 'visibilitychange', resumeUpload);

    /* bfcache return after a successful add (old js:387-398): the design becomes
       an edit of the line that was just added, with a fresh Worker session. */
    on(window, 'pageshow', (event) => {
      if (!event.persisted || !submission.finished) return;
      editingLineId = sessionLineId;
      sessionLineId = newLineId();
      photoToken = randHex(16);
      abortUpload();
      resetUploaded();
      submission = { attempted: false, confirmed: false, items: null };
      atcBusy = false;
      setAtcBusyUi(false);
      root.inert = false;
      render(store.getState());
    });

    /* Action bar (visual pass): the nav of each step is position:sticky at the
       viewport bottom; .is-stuck marks the moment it floats over the options,
       and only then does CSS give it glass. rootMargin -1px: a stuck bar ends
       exactly at the viewport edge, so it is 1px outside the root (ratio < 1),
       while a bar resting in place is fully inside (ratio 1). */
    if ('IntersectionObserver' in window) {
      const navObserver = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const r = e.boundingClientRect;
          const rb = e.rootBounds;
          const stuck = !!rb && e.isIntersecting && e.intersectionRatio < 1 && r.bottom > rb.bottom - 0.5 && r.top > rb.top;
          e.target.classList.toggle('is-stuck', stuck);
        });
      }, { threshold: [0, 1], rootMargin: '0px 0px -1px 0px' });
      $$('.dv-cz__nav').forEach((nav) => navObserver.observe(nav));
      root.classList.add('dv-cz--nav-io');
      cleanups.push(() => { navObserver.disconnect(); root.classList.remove('dv-cz--nav-io'); });
    }

    /* Phones, Gift step: CSS pins the preview under the header (position:
       sticky, css "Phones, Gift step") inside .dv-cz__pin = preview + step
       heading, so it lets go where the options begin (owner 2026-09-30: the
       preview's lower edge never goes below "Lid composition" / the card
       options, it never covers them). JS only marks .is-stuck — a soft edge
       while the heading passes under the pinned preview. The former
       ".dv-cz--typing" release is gone: a text field can no longer end up
       under the preview. */
    const pinLeads = $$('[data-dv-cz-panel="gift"] .dv-cz__lead');
    const pinMq = window.matchMedia ? window.matchMedia('(max-width: 700px) and (min-height: 560px)') : { matches: false }; // = css
    let pinRaf = 0;
    function syncPin() {
      pinRaf = 0;
      if (destroyed) return;
      pinLeads.forEach((lead) => {
        let stuck = false;
        if (pinMq.matches && lead.getClientRects().length) {
          const cs = window.getComputedStyle(lead);
          const r = lead.getBoundingClientRect();
          const box = lead.parentElement.getBoundingClientRect();
          stuck = cs.position === 'sticky' && Math.abs(r.top - parseFloat(cs.top)) < 1 && box.bottom > r.bottom + 1;
        }
        lead.classList.toggle('is-stuck', stuck);
      });
    }
    const queuePin = () => { if (!pinRaf) pinRaf = window.requestAnimationFrame(syncPin); };
    on(window, 'scroll', () => { if (STEPS[store.getState().step] === 'gift') queuePin(); }, { passive: true });
    cleanups.push(() => { window.cancelAnimationFrame(pinRaf); });

    initGestures();
    const unsubscribe = store.subscribe(render);
    cleanups.push(unsubscribe);

    /* ------------------------------------------------------------ boot */

    const params = new URLSearchParams(window.location.search);
    const editId = params.get('edit');
    const setParam = params.get('set');

    /* Child lines of the edited line that the customizer does not rebuild from
       _design (review fix: "Update in cart" deleted them with the old line).
       Glue on a Mini line becomes the Review's glue option again; a bag or
       magnets child already recorded in a legacy design is rebuilt from
       extras; everything else the drawer attached (bag, glue on a Classic
       line…) is carried over unchanged. no-logo and post-card always follow
       the design. */
    function keptChildren(cart, oldId, d) {
      const kept = [];
      let glue = false;
      const is = (it, a) => !!(ADDONS[a] && Number(ADDONS[a].variant_id) && Number(it.variant_id) === Number(ADDONS[a].variant_id));
      (cart.items || []).forEach((it) => {
        const p = it.properties || {};
        if (p._parent_line !== oldId) return;
        if (is(it, 'nologo') || is(it, 'card')) return;
        if (is(it, 'glue') && isMini(d.set)) { glue = true; return; }
        if (is(it, 'bag') && d.extras && d.extras.bag) return;
        if (is(it, 'magnets') && d.extras && d.extras.magnets) return;
        const properties = {};
        Object.keys(p).forEach((key) => { if (key !== '_parent_line') properties[key] = p[key]; });
        const unit = Number(it.original_price != null ? it.original_price : it.price) || 0;
        kept.push({ id: Number(it.variant_id || it.id), quantity: Number(it.quantity) || 1, properties, title: String(it.product_title || it.title || ''), price: unit });
      });
      return { kept, glue };
    }

    function bootEdit(oldId) {
      try { window.history.replaceState(null, '', window.location.pathname); } catch (e) { /* ignore */ }
      return fetchT(SHOP_ROOT + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }, CART_TIMEOUT_MS)
        .then((r) => { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); })
        .then((cart) => {
          const item = (cart.items || []).find((it) => (it.properties || {})._line_id === oldId && (it.properties || {})._design);
          if (!item) return false;
          const d = JSON.parse(item.properties._design);
          editingLineId = oldId;
          const set = SETS[d.set] ? d.set : 'classic'; // v1 designs are Classic 1000
          const children = keptChildren(cart, oldId, { ...d, set });
          const box = { ...defaultBox(), ...(d.box || {}) };
          if (String(box.color).toUpperCase() === LEGACY_SILVER) box.color = SILVER;
          if (!FONTS[box.font]) box.font = 'cormorant';
          const card = d.card
            ? { added: true, text: d.card.text || '', font: d.card.font || 'cormorant', sizePreset: d.card.sizePreset || 'l', color: String(d.card.color || BLACK).toUpperCase() === LEGACY_SILVER ? SILVER : (d.card.color || BLACK), align: d.card.align || 'justify', orient: d.card.orientation === 'h' || d.card.orientation === 'v' ? d.card.orientation : 'auto' }
            : defaultCard();
          const crop = (d.crops && d.crops.puzzle) || d.crop;
          store.set({
            set: selectable(set) ? set : null,
            orientation: d.orientation === 'h' ? 'h' : 'v',
            crop: crop && typeof crop.zoom === 'number' ? { zoom: crop.zoom, nx: Number(crop.nx) || 0, ny: Number(crop.ny) || 0 } : { zoom: 1, nx: 0, ny: 0 },
            box,
            card,
            nfc: d.nfc ? { enabled: true, url: String(d.nfc) } : { enabled: false, url: '' },
            addons: { glue: !!(d.addons && d.addons.glue) || children.glue },
            extras: { bag: !!(d.extras && d.extras.bag), magnets: !!(d.extras && d.extras.magnets), frameInterest: !!(d.extras && d.extras.frameInterest) },
            kept: children.kept,
            step: selectable(set) ? 1 : 0,
            maxStepReached: selectable(set) ? 1 : 0,
          });
          if (!selectable(set) && els.setNotice) {
            els.setNotice.textContent = t('dv_cz_js_edit_set', 'Your saved set is not available right now — please choose a set. Your design is kept.');
            els.setNotice.hidden = false;
          }
          const pd = d.photo || {};
          const fromCloud = () => {
            if (!pd.originalUrl) return Promise.resolve(false);
            return fetchT(pd.originalUrl, {}, 120000)
              .then((r) => { if (!r.ok) throw new Error('cloud ' + r.status); return r.blob(); })
              .then((blob) => {
                const type = blob.type && /^image\//.test(blob.type) ? blob.type : 'image/jpeg';
                acceptFile(new Blob([blob], { type }), true, false, pd.origin || null);
                return true;
              })
              .catch((e) => { console.warn('[dv-customizer] cloud photo fetch', e); return false; });
          };
          const restoreFailed = () => { if (els.editNote) els.editNote.hidden = false; };
          return idbGet('photo_' + oldId).then((rec) => {
            const blob = recordToBlob(rec);
            if (blob) { acceptFile(blob, true, false, pd.origin || null); return true; }
            return fromCloud().then((ok) => { if (!ok) restoreFailed(); return true; });
          }).catch((e) => {
            console.error('[dv-customizer] idb read', e);
            return fromCloud().then((ok) => { if (!ok) restoreFailed(); return true; });
          });
        })
        .catch((e) => { console.error('[dv-customizer] edit boot', e); return false; });
    }

    function bootPdpPending() {
      // Photo handed over by the PDP (daile-pdp.js:551-607); accepted as a normal upload.
      return idbGet('photo_pdp-pending').then((rec) => {
        if (!rec) return undefined;
        const blob = recordToBlob(rec);
        const clear = () => idbPut('photo_pdp-pending', null).catch(() => {});
        if (!blob || !blob.type) { clear(); return undefined; }
        /* Fix 2026-10-11: the Blob read from IndexedDB may be backed by a file that WebKit drops
           with the record; it is copied into memory before the record is cleared, and that copy
           is the one uploaded at Add to cart. */
        /* 1.3.1: the STUDIO page leaves sessionStorage 'daile-studio-handoff' { id, style, mode, result_url,
           print_url, w, h, at } just before this hand-off (assets/mn-studio.js toPuzzle); read once, then removed,
           so a later photo from the PDP is not taken for an artwork. Older than 10 min → ignored; acceptFile also
           requires the picture's size to be the artwork's ×1…×4 (studioSizeMatch). */
        let origin = null;
        try {
          const ho = JSON.parse(window.sessionStorage.getItem('daile-studio-handoff') || 'null');
          window.sessionStorage.removeItem('daile-studio-handoff');
          if (ho && Date.now() - (Number(ho.at) || 0) < 10 * 60 * 1000) {
            origin = {
              source: 'studio', style: String(ho.style || '').slice(0, 40), id: String(ho.id || '').slice(0, 80),
              mode: ho.mode === 'live' ? 'live' : 'mock',
              result_url: String(ho.result_url || '').slice(0, 300), print_url: String(ho.print_url || '').slice(0, 300),
              w: Number(ho.w) || null, h: Number(ho.h) || null,
            };
          }
        } catch (e) { /* no sessionStorage — treated as an own photo */ }
        return memoryCopy(blob).then((photo) => {
          clear();
          if (destroyed) return;
          acceptFile(photo, false, false, origin);
          if (!store.getState().set && els.setNotice) {
            els.setNotice.textContent = t('dv_cz_js_photo_ready', 'Your photo is ready — now choose your set.');
            els.setNotice.hidden = false;
          }
        });
      }).catch((e) => console.warn('[dv-customizer] pdp pending read', e));
    }

    /* ?set= / ?edit=: an inline script in the section hid the set grid until
       now (dv-cz--boot), so the grid does not flash before the builder. */
    const endBoot = () => root.classList.remove('dv-cz--boot');
    cleanups.push(endBoot);

    render(store.getState());
    if (editId) {
      // edit wins over ?set= and the PDP hand-over (old bug B6 fixed: decided before the URL is cleaned)
      bootEdit(editId).then(endBoot, endBoot);
    } else {
      if (setParam && SETS[setParam]) {
        if (selectable(setParam)) {
          store.set({ set: setParam });
          /* PDP custom-puzzle hand-off (2026-09-29): ?glue=1 preselects the Mini glue add-on
             offered at the Review step (same state as its "Add" button). Ignored for Classic /
             Signature; an unavailable glue kit stays hidden and out of the cart (addonOk). */
          if (params.get('glue') === '1' && isMini(setParam)) store.merge('addons', { glue: true });
          goTo(1, { noScroll: true });
        } else if (els.setNotice) {
          els.setNotice.textContent = t('dv_cz_js_set_unavailable', 'This set cannot be ordered yet. Please choose another set.');
          els.setNotice.hidden = false;
        }
        try {
          params.delete('set');
          params.delete('glue');
          const rest = params.toString();
          window.history.replaceState(null, '', window.location.pathname + (rest ? '?' + rest : '') + window.location.hash);
        } catch (e) { /* ignore */ }
      }
      endBoot();
      if (/[?&]pdp=1\b/.test(window.location.search)) {
        try { window.history.replaceState(null, '', window.location.pathname); } catch (e) { /* ignore */ }
      }
      bootPdpPending();
    }
    track('personalizer_started', { size: store.getState().set ? String(SETS[store.getState().set].pieces) : '', set: store.getState().set || '' });

    const api = {
      store,
      getState: store.getState,
      chooseSet,
      goTo,
      acceptFile,
      buildDesign: () => buildDesign(store.getState(), true),
      views,
      destroy() {
        destroyed = true;
        cleanups.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
        Object.values(views).forEach((v) => {
          if (v.ac) v.ac.abort();
          window.clearTimeout(v.timer);
          window.clearTimeout(v.busyT);
          window.clearTimeout(v.retryT);
          window.cancelAnimationFrame(v.raf);
        });
        abortUpload();
        window.clearTimeout(liveTimer);
        window.clearTimeout(processingT);
        if (proxyUrl) URL.revokeObjectURL(proxyUrl);
        root.dvCzMounted = false;
        if (window.DVCustomizer && window.DVCustomizer.instance === api) window.DVCustomizer.instance = null;
      },
    };
    window.DVCustomizer.instance = api;
    return api;
  }

  window.DVCustomizer = {
    version: VERSION,
    instance: null,
    mount,
    // pure helpers, exposed for tests
    _test: { cropRect, clampCrop, qualityFor, trimFor, bleedFor, SETS, nfcProblem, nfcNormalize, fetchT },
  };

  if (window.DV && typeof window.DV.register === 'function') {
    window.DV.register('dv-customizer', (section) => {
      const instance = mount(section);
      return instance ? { destroy: instance.destroy } : undefined;
    });
  } else {
    const boot = () => document.querySelectorAll('[data-dv-customizer]').forEach((el) => mount(el));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }
})();
